/**
 * MonthlyPlanPage - Monatsplan mit 100 Posts
 * 
 * Zeigt einen kalendarischen Überblick über geplante Posts
 * mit natürlichem Timing und Smart Scheduling.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { trackEvent } from "../lib/trackEvent";
import { toast } from 'sonner';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, Calendar, Sparkles, Loader2, X, Plus,
  Eye, Download, CheckCircle, Clock, TrendingUp, Play, Trash2, Edit2,
  Facebook, Instagram, Linkedin, Send, RefreshCw, Video, Lightbulb,
  Search, Share2, FileText, FileDown, Megaphone, Image, Zap,
  AlertTriangle, ShieldCheck, Copy, Wifi
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { TREND_RESEARCH_ENABLED, VIDEO_BATCH_ENABLED, QA_UI_ENABLED } from "../config/features";
import { FacebookPreviewModal, LinkedInPreviewModal, OutputFormatModal, TrendResearchModal, ContentRow } from '../App';
import AutopilotWizard from '../components/AutopilotWizard';
import CalendarTour from '../components/CalendarTour';
import AnalyticsInsights from '../components/AnalyticsInsights';
import AutopilotRecommendations from '../components/AutopilotRecommendations';
import PlanRefreshComparison from '../components/PlanRefreshComparison';
import AutopilotHistory from '../components/AutopilotHistory';
import PublishPreviewDialog from '../components/PublishPreviewDialog';
import BatchRefreshPanel from '../components/BatchRefreshPanel';
import OneClickPlanButton from '../components/OneClickPlanButton';
import PreparedPostsPanel from '../components/PreparedPostsPanel';
import useIsMaster from '../hooks/useIsMaster';
import { direktPostNutzlast } from '../components/social/direktPostNutzlast';
import { fachfreigabeModell, zaehleWartende } from "./monthlyPlan/fachfreigabeModell";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Leerformular für „Neuen Post erstellen". Als Konstante, weil das Formular an vier
// Stellen frisch geöffnet wird — ein vergessenes Feld hätte sonst Reste des letzten
// Entwurfs übernommen.
const EMPTY_NEW_POST_FIELDS = {
  hook: "", core_content: "", time: "12:00",
  platform: "instagram", content_type: "post", pillar: "",
};

// Maximale Bildgröße für eigene Post-Bilder — identisch zur Server-Prüfung (15 MB).
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Maximale Videogröße für eigene Post-Videos (Reels) — identisch zur Server-Prüfung (200 MB).
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"]; // quicktime = .mov

// Wandelt eine Axios-Fehlerantwort in eine sicher anzeigbare Meldung um.
// FastAPI liefert bei 422 ein detail-ARRAY/Objekt → niemals roh rendern (sonst
// "[object Object]" bzw. React-Crash). Nur einen echten String-Detail zeigen,
// sonst eine klare deutsche Fallback-Meldung.
const coerceError = (err, fallback = "Etwas ist schiefgelaufen. Bitte versuche es erneut.") => {
  const data = err?.response?.data;
  const d = (data && (data.user_message || data.detail));
  return (typeof d === "string" && d.trim()) ? d : fallback;
};

// Relative Medienpfade (/api/media/file/…?t=…) gegen den Backend-Host auflösen —
// dieselbe Regel wie überall im Frontend. Absolute URLs (https:, data:, blob:)
// bleiben unangetastet.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaSrc = (url) =>
  !url ? "" : /^(https?:|data:|blob:)/i.test(url) ? url : `${BACKEND}${url}`;

// Platform colors for badges
const platformColors = {
  facebook: 'bg-blue-600 text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  linkedin: 'bg-sky-700 text-white',
  tiktok: 'bg-gray-100 text-black',
  google_business: 'bg-orange-50 text-orange-600',
  // Threads: Meta-Corporate-Black, passend zur Threads-Brand-Identity.
  threads: 'bg-stone-900 text-white'
};


const MonthlyPlanPage = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const wizardRef = useRef(null);
  const scrollToWizard = () =>
    wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Video ist KEIN Kundenprodukt: alle Video-Oberflächen (Toggle, Post-Badge,
  // Kurzvideo-Panel, Kalender-Punkt) sieht nur der Inhaber. Fail-closed —
  // solange die Rolle unbekannt ist, gilt „kein Master".
  const isMaster = useIsMaster();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(null); // post_id being generated
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [monthlyPosts, setMonthlyPosts] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [stats, setStats] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoLogs, setVideoLogs] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [includeVideos, setIncludeVideos] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showVideoLogs, setShowVideoLogs] = useState(false);
  // Social Media Auto-Posting States
  const [socialStatus, setSocialStatus] = useState(null);
  // Mandantenbezogener Verbindungszustand (GET /social-connect/status) — siehe istVerbunden().
  const [connectStatus, setConnectStatus] = useState(null);
  const [socialLogs, setSocialLogs] = useState([]);
  const [showSocialLogs, setShowSocialLogs] = useState(false);
  const [postingPlatform, setPostingPlatform] = useState(null); // 'facebook' | 'linkedin' | null
  // Smart Scheduling State
  const [smartScheduleStatus, setSmartScheduleStatus] = useState(null);
  const [lastScheduleUpdate, setLastScheduleUpdate] = useState(null);
  // Posting Preview Modal States
  const [previewPost, setPreviewPost] = useState(null);
  const [previewPlatform, setPreviewPlatform] = useState(null); // 'facebook' | 'linkedin'
  // Blog/WhatsApp Output Modal State
  const [outputPost, setOutputPost] = useState(null);
  // Perplexity Trend Modal State
  const [showTrendModal, setShowTrendModal] = useState(false);
  // Live Posting State
  // Publish-Preview-Dialog State (Kalender 2.4)
  const [publishPreviewPost, setPublishPreviewPost] = useState(null);
  // Modus des Dialogs: "publish" = Veröffentlichen-Weg (Bestand), "preview" = reine Vorschau
  const [previewMode, setPreviewMode] = useState("publish");
  // Einplanen-Dialog (Übergang approved -> scheduled). Nur genau EIN PATCH, Backend ist die Wahrheit.
  const [schedulingPost, setSchedulingPost]     = useState(null);            // Post im Dialog oder null
  const [scheduleFields, setScheduleFields]     = useState({ date: "", time: "" }); // YYYY-MM-DD / HH:MM
  const [scheduleError, setScheduleError]       = useState("");              // deutscher Fehlertext (JS-Vorprüfung oder Backend-detail)
  const [schedulingSaving, setSchedulingSaving] = useState(false);
  // Kalender 2.0 — Inline Editing & Manual Posts
  const [editingPostId, setEditingPostId]   = useState(null);
  const [editFields, setEditFields]         = useState({});
  const [savingEdit, setSavingEdit]         = useState(false);
  // Bild nachreichen (beim Bearbeiten) & Karten neu erzeugen — postId, der gerade läuft
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [uploadingVideoFor, setUploadingVideoFor] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);       // 0–100
  const [regeneratingCardsFor, setRegeneratingCardsFor] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [newPostFields, setNewPostFields]   = useState(EMPTY_NEW_POST_FIELDS);
  const [savingNewPost, setSavingNewPost]   = useState(false);
  // Eigenes Bild zum manuellen Post (wird erst NACH dem Anlegen hochgeladen)
  const [newPostFile, setNewPostFile]       = useState(null);
  const [newPostFilePreview, setNewPostFilePreview] = useState(null); // ObjectURL, nur lokal
  const [newPostFileError, setNewPostFileError]     = useState(null);
  const newPostFileInputRef = useRef(null);
  // Kalender 2.1 — Review & Freigabe
  const [notesValues, setNotesValues]       = useState({}); // { postId: localDraftText }
  const [savingNotes, setSavingNotes]       = useState(null); // postId being saved
  // Kalender 2.2 — Externe Freigabe-Links
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [shareLinks, setShareLinks]         = useState([]);
  const [creatingLink, setCreatingLink]     = useState(false);
  const [shareLabel, setShareLabel]         = useState("");
  // Kalender 2.3 — Aktivitäts-Feed (Freigaben & Kommentare vom Kunden)
  const [activityItems, setActivityItems]   = useState([]);
  const [unreadActivity, setUnreadActivity] = useState(0);
  const [loadingActivity, setLoadingActivity] = useState(false);
  // Kalender 2.4 — Content-Varianten pro Post
  const [variantsMap, setVariantsMap]       = useState({}); // { postId: { items, loading, error } }
  const [adoptingVariant, setAdoptingVariant] = useState(null); // postId being adopted
  // Kalender 2.5 — Qualitätsprüfung
  const [auditData, setAuditData]           = useState(null);   // full API response
  const [auditLoading, setAuditLoading]     = useState(false);
  // Kalender 2.6 — Batch-Refresh-Panel
  const [batchOpen, setBatchOpen]           = useState(false);

  // ── Kalender-Tour (nach Onboarding) ────────────────────────────────────────
  const [tourActive, setTourActive] = useState(false);

  // Tour einmalig beim Laden aktivieren wenn fromOnboarding gesetzt
  useEffect(() => {
    if (location.state?.fromOnboarding) {
      trackEvent("monthly_plan_first_viewed", { source: "onboarding" });
      // State sofort löschen (kein Re-trigger bei Reload)
      navigate(location.pathname, { replace: true, state: {} });
      // Tour leicht verzögert starten (damit Kalender erst lädt)
      const timerId = setTimeout(() => setTourActive(true), 1200);
      return () => clearTimeout(timerId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── A) Refetch nach Video-Generierung ─────────────────────────────────────
  // VideoPage navigiert mit { state: { refreshPlan: true, updatedPostId } } zurück.
  // location.key wechselt bei jeder navigate()-Navigation — das ist der zuverlässige
  // Trigger auch wenn der Pfad identisch bleibt.
  useEffect(() => {
    if (location.state?.refreshPlan) {
      fetchCalendar();
      // Flag konsumieren: navigate(replace) erhält React Router-interne Fields
      // (key, idx) — window.history.replaceState({}) würde diese zerstören.
      navigate(location.pathname, { replace: true, state: {} });
    }
  // fetchCalendar ist stabil (useCallback) — location.key als Trigger genügt
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Bonus: Refetch wenn User von einem anderen Tab zurückkehrt (Tab-Wechsel-Muster)
  useEffect(() => {
    const onFocus = () => {
      // Nur neu laden wenn Seite wirklich sichtbar ist (kein Hintergrund-Tab)
      if (document.visibilityState === "visible") fetchCalendar();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bonus-Posts im aktuellen Monat vorhanden?
  const hasBonusPosts = useMemo(
    () => monthlyPosts.some(p => p.source === "foerderfuchs_bonus"),
    [monthlyPosts]
  );

  // ── Monatsbezug: „Gibt es für DIESEN Monat schon einen Plan?" ──────────────
  // Bewusst NICHT über total_posts der API: dort zählen auch Legacy-Posts fremder
  // Monate mit, der Wert wäre als Plan-Signal falsch-positiv.
  const monthPrefix = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;

  const postsThisMonth = useMemo(
    () => monthlyPosts.filter(p => String(p.scheduled_date || "").startsWith(monthPrefix)),
    [monthlyPosts, monthPrefix]
  );

  // Wie viele Beiträge des geladenen Monats hängen am Fachfreigabe-Tor?
  // `monthlyPosts` ist die einzige Quelle mit VOLLEN Beiträgen (die Kalender-Einträge
  // sind Fragmente ohne die Felder, die hier zählen).
  const wartendeFachfreigaben = useMemo(() => zaehleWartende(monthlyPosts), [monthlyPosts]);

  const hasPlanForMonth = useMemo(
    () =>
      postsThisMonth.length > 0 ||
      Object.entries(calendarData).some(
        ([d, arr]) => d.startsWith(monthPrefix) && (arr || []).length > 0
      ),
    [postsThisMonth, calendarData, monthPrefix]
  );

  // Interne Qualitätsprüfung: läuft im System, nicht in der Kundenansicht.
  // EINE Bedingung für alle QA-Oberflächen (Button, Audit-Panel, Dots, Hinweise).
  const showQaUi = QA_UI_ENABLED;

  const themeColors = {
    'Gründung': 'bg-blue-100 text-blue-700 border-blue-200',
    'Gründungscoaching': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Bewerbung': 'bg-green-100 text-green-700 border-green-200',
    'Bewerbungscoaching': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Bewerbungsfoto App': 'bg-purple-100 text-purple-700 border-purple-200',
    'Motivation': 'bg-amber-100 text-amber-700 border-amber-200'
  };

  const fetchVideoStatus = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axios.get(`${API}/video/status`),
        axios.get(`${API}/video/logs?limit=10`)
      ]);
      setVideoStatus(statusRes.data);
      setVideoLogs(logsRes.data?.logs || []);
    } catch (error) {
      { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
    }
  }, []);

  const fetchSocialStatus = useCallback(async () => {
    try {
      // `/social-connect/status` ist die MANDANTENBEZOGENE Wahrheit — dieselbe Quelle,
      // die die Kanäle-Seite liest. `/social/all/status` liefert dagegen den
      // Prozess-Umgebungs-Zustand (facebook_poster.is_configured = FB_PAGE_ID und
      // META_ACCESS_TOKEN aus os.environ) und weiß nichts vom eingeloggten Workspace.
      // Es wird weiter geladen, weil es die Nutzungszähler trägt — der VERBUNDEN-Zustand
      // kommt aber ausschließlich aus connectStatus.
      const [statusRes, connectRes, logsRes, smartRes, lastUpdateRes] = await Promise.all([
        axios.get(`${API}/social/all/status`),
        axios.get(`${API}/social-connect/status`),
        axios.get(`${API}/social/all/logs?limit=20`),
        axios.get(`${API}/smart-schedule/status`),
        axios.get(`${API}/smart-schedule/last-update`)
      ]);
      setSocialStatus(statusRes.data);
      setConnectStatus(connectRes.data);
      setSocialLogs(logsRes.data?.logs || []);
      setSmartScheduleStatus(smartRes.data);
      setLastScheduleUpdate(lastUpdateRes.data);
    } catch (error) {
      { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
    }
  }, []);

  /**
   * Ist DIESER Workspace mit der Plattform verbunden?
   *
   * Befund 30./31.07.2026: Diese Karten zeigten „🟢 Verbunden" bzw. „⚪ nicht verbunden"
   * anhand von `socialStatus.<platform>.status.configured`. Dieses Flag stammt aus
   * `facebook_poster.is_configured` und liest PROZESS-UMGEBUNGSVARIABLEN, nicht den Token
   * des eingeloggten Workspace. Folgen, beide auf Kamera sichtbar:
   *   - Sobald jemand FB_PAGE_ID setzt, sieht JEDER Kunde „Facebook verbunden" —
   *     auch einer, der gar keinen Token hat.
   *   - Nach einem echten OAuth-Verbinden blieb die Karte auf „nicht verbunden", weil der
   *     Callback in social_oauth_tokens schreibt und nicht in die Umgebung.
   * Für das Meta-App-Review-Video ist das heikel: Dieses Widget trägt id="veroeffentlichen"
   * und ist das Sprungziel des Kopfzeilen-Links — es lässt sich nicht wegscrollen. Ein
   * Reviewer sähe hier eine Anzeige, die dem gerade gezeigten Verbinden-Ablauf widerspricht.
   *
   * `not_connected` ist der einzige Zustand, der NICHT verbunden heißt. `connecting`,
   * `token_expired`, `reconnect_needed` usw. sind Verbindungen mit Problem — die werden
   * bewusst nicht als „nicht verbunden" beschönigt.
   */
  const istVerbunden = useCallback((platform) => {
    const p = connectStatus?.platforms?.[platform];
    if (!p) return false;
    return p.status && p.status !== 'not_connected';
  }, [connectStatus]);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/monthly-plan/calendar?month=${selectedMonth}&year=${selectedYear}`);
      setCalendarData(response.data.calendar || {});
      // Die Kalender-Einträge sind Fragmente (Hook auf 40 Zeichen gekürzt, kein
      // Textkörper, keine Hashtags/Medien). Nur dieses posts-Array trägt die vollen
      // Beiträge — es speist Panel und Vorschau.
      const posts = response.data.posts || [];
      setMonthlyPosts(posts);
      // Rückgabe der frisch geladenen Posts: die Aktivierungs-Zusammenfassung liest
      // daraus die neuen Status (setState wäre im selben Tick noch nicht sichtbar).
      return posts;
    } catch (error) {
      { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // ── Beitrag löschen (CONTENT-02) ──────────────────────────────────────────
  // Bis 16.08.2026 gab es keinen Weg, einen Beitrag wieder loszuwerden — weder hier
  // noch im Backend. Ein Fehlgriff blieb für immer im Kalender stehen.
  // Veröffentlichtes lehnt der Server mit 409 ab; die Meldung von dort wird
  // unverändert gezeigt, weil sie den Grund besser erklärt als ein Pauschaltext.
  const handleDeletePost = useCallback(async (post) => {
    const label = (post.hook_full || post.hook || "").slice(0, 60);
    if (!window.confirm(
      `Diesen Beitrag wirklich löschen?\n\n„${label}${label.length >= 60 ? "…" : ""}"\n\n` +
      `Das lässt sich nicht rückgängig machen.`
    )) return;
    setDeletingPostId(post.id);
    try {
      await axios.delete(`${API}/monthly-plan/${post.id}`);
      toast.success("Beitrag gelöscht");
      await fetchCalendar();
    } catch (error) {
      toast.error(coerceError(error, "Der Beitrag konnte nicht gelöscht werden."));
    } finally {
      setDeletingPostId(null);
    }
  }, [fetchCalendar]);

  // ── Ehrliche Aktivierungs-Zusammenfassung (nach 1-Klick-Plan) ──────────────
  // Zählt aus den frisch geladenen Monats-Posts: wie viele automatisch eingeplant
  // wurden ('scheduled') und wie viele eine Ausnahme sind (Termin vergangen /
  // Kanal nicht verbunden / Tarif pausiert). KEINE neue Pflicht-Interaktion —
  // nur ein ehrlicher Toast.
  // Ist AUTO_SCHEDULE_ON_PLAN (Backend-Flag) AUS, wird nichts automatisch
  // 'scheduled' → scheduled=0 → es erscheint KEINE Zusatzmeldung (Verhalten wie heute).
  const showActivationSummary = useCallback((posts) => {
    if (!Array.isArray(posts)) return;
    const ATTENTION = ['needs_reschedule', 'blocked_channel', 'paused_plan'];
    const prefix = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;
    const monthPosts = posts.filter(p => String(p.scheduled_date || "").startsWith(prefix));
    const scheduled = monthPosts.filter(p => p.status === 'scheduled').length;
    const attention = monthPosts.filter(p => ATTENTION.includes(p.status)).length;
    // Nichts automatisch eingeplant → keine zusätzliche Meldung.
    if (scheduled === 0) return;
    if (attention === 0) {
      toast.success(`${scheduled} ${scheduled === 1 ? 'Beitrag' : 'Beiträge'} automatisch eingeplant 🦊`);
    } else {
      toast.warning(
        `${scheduled} ${scheduled === 1 ? 'Beitrag' : 'Beiträge'} automatisch eingeplant · ` +
        `${attention} ${attention === 1 ? 'braucht' : 'brauchen'} deine Aufmerksamkeit.`
      );
    }
  }, [selectedYear, selectedMonth]);

  // Nach erfolgreichem 1-Klick-Plan: frisch laden UND ehrliche Kurzmeldung zeigen.
  const handlePlanActivated = useCallback(async () => {
    const posts = await fetchCalendar();
    showActivationSummary(posts);
  }, [fetchCalendar, showActivationSummary]);

  const fetchActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const res = await axios.get(`${API}/share/activity?month=${selectedMonth}&year=${selectedYear}&limit=30`);
      setActivityItems(res.data.items || []);
      setUnreadActivity(res.data.unread_count || 0);
    } catch { /* still works if endpoint not yet available */ }
    finally { setLoadingActivity(false); }
  }, [selectedMonth, selectedYear]);

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/share/activity/mark-read`, { month: selectedMonth, year: selectedYear });
      setUnreadActivity(0);
      setActivityItems(prev => prev.map(item => ({ ...item, read: true })));
    } catch { /* silent */ }
  };

  const handleJumpToPost = (postDate) => {
    // postDate is "YYYY-MM-DD"
    if (!postDate) return;
    const parts = postDate.split("-");
    if (parts.length < 3) return;
    const day = parseInt(parts[2], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[0], 10);
    if (month !== selectedMonth || year !== selectedYear) return;
    setSelectedDay(day);
    setShowNewPostForm(false);
    setEditingPostId(null);
    setTimeout(() => {
      document.getElementById("kalender-bereich")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const formatActivityTime = (iso) => {
    if (!iso) return "";
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return "Gerade eben";
      if (mins < 60) return `vor ${mins} Min.`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `vor ${hours} Std.`;
      const days = Math.floor(hours / 24);
      return `vor ${days} Tag${days === 1 ? "" : "en"}`;
    } catch { return ""; }
  };

  useEffect(() => {
    fetchCalendar();
    fetchSocialStatus();
    fetchActivity();
  }, [fetchCalendar, fetchSocialStatus, fetchActivity]);

  // Video-Status NUR für den Inhaber holen — die Route ist ein Inhaber-Werkzeug und
  // lief bisher bei jedem Kunden mit. Bewusst ein eigener Effekt: sonst würden die
  // Kunden-Abrufe oben ein zweites Mal laufen, sobald die Rolle geklärt ist.
  useEffect(() => {
    if (isMaster) fetchVideoStatus();
  }, [isMaster, fetchVideoStatus]);

  const handleGenerateVideo = async (post) => {
    setGeneratingVideo(post.id);
    try {
      const response = await axios.post(`${API}/video/generate`, {
        post_id: post.id,
        topic: post.topic || post.title || 'Förderfuchs Content',
        text: post.content || post.text || '',
        channel: post.channel || 'instagram',
        image_idea: post.image_idea
      });
      
      if (response.data.success) {
        toast.success('Video generiert! ' + (response.data.status === 'simulated' ? '(Vorschau)' : ''));
        fetchVideoStatus();
        fetchCalendar();
      } else {
        toast.error(response.data.message || 'Video-Generierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler: ' + coerceError(error));
    } finally {
      setGeneratingVideo(null);
    }
  };

  // Evergreen Quick Toggle Handler
  const handleEvergreenQuickToggle = async (post) => {
    const newState = !post.is_evergreen;
    try {
      await axios.put(`${API}/evergreen/${post.id}/quick-toggle`, {
        is_evergreen: newState,
        interval_days: 30 // Default 1 Monat
      });
      toast.success(newState ? '♻️ Als Evergreen markiert' : 'Evergreen deaktiviert');
      fetchCalendar();
    } catch (error) {
      toast.error('Fehler beim Ändern des Evergreen-Status');
    }
  };

  // Live-Posting Handler für geplante Posts
  const handleGenerateAllVideos = async () => {
    // Sammle alle Post-IDs aus dem Kalender
    const allPosts = Object.values(calendarData).flat();
    const postIds = allPosts.map(p => p.id).filter(Boolean);
    
    if (postIds.length === 0) {
      toast.error('Keine Posts zum Generieren gefunden');
      return;
    }
    
    if (!window.confirm(`⚠️ ACHTUNG: Dies generiert Videos für ${postIds.length} Posts.\n\nDies kann Credits/Compute kosten!\n\nFortfahren?`)) {
      return;
    }
    
    setBatchGenerating(true);
    try {
      const response = await axios.post(`${API}/video/generate-batch`, {
        post_ids: postIds.slice(0, 20), // Max 20
        confirm_cost: true
      });
      
      if (response.data.success) {
        const summary = response.data.summary;
        toast.success(`Batch abgeschlossen: ${summary.successful}/${summary.total} Videos generiert`);
        fetchVideoStatus();
        fetchCalendar();
      } else {
        toast.error(response.data.message || 'Batch-Generierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler: ' + coerceError(error));
    } finally {
      setBatchGenerating(false);
    }
  };

  const handleGenerateMonthlyPlan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/monthly-plan/generate`, {
        month: selectedMonth,
        year: selectedYear,
        // Ohne sichtbaren Umschalter darf der Kunde nicht stillschweigend auf
        // "mit Videos" laufen — der Schalter existiert nur für den Inhaber.
        include_videos: isMaster ? includeVideos : false
      });
      setStats(response.data);
      toast.success(`${response.data.total_posts} Posts generiert!`);
      fetchCalendar();
    } catch (error) {
      toast.error('Fehler: ' + coerceError(error));
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchSocialPost = async (platform) => {
    // Die Kalender-Eintraege sind Anzeige-Fragmente: der Hook endet nach 40 Zeichen mit
    // „…", einen Textkoerper haben sie gar nicht (server.py:1168). Gesendet wird aus den
    // vollstaendigen Beitraegen desselben Monats.
    const allPosts = postsThisMonth;
    if (allPosts.length === 0) {
      toast.error(t('Keine Posts gefunden', 'No posts found'));
      return;
    }

    const platformLabel = platform === 'facebook' ? 'Facebook' : 'LinkedIn';
    const postsToProcess = allPosts.slice(0, 5);
    const count = postsToProcess.length;

    if (!window.confirm(
      t(
        `⚠️ ECHT VERÖFFENTLICHEN: ${count} Post${count === 1 ? '' : 's'} auf ${platformLabel}?\n\n` +
        `Die Posts werden sofort öffentlich sichtbar – kein Entwurf, sondern wirklich live.\n\n` +
        `Videos werden übersprungen (FAL_KEY-Setup ausstehend).`,
        `⚠️ PUBLISH FOR REAL: ${count} post${count === 1 ? '' : 's'} to ${platformLabel}?\n\n` +
        `The posts will be visible to the public immediately – not a draft, truly live.\n\n` +
        `Videos will be skipped (FAL_KEY setup pending).`,
      )
    )) {
      return;
    }

    setPostingPlatform(platform);
    try {
      let successCount = 0;
      const failures = [];

      for (const post of postsToProcess) {
        const nutzlast = direktPostNutzlast(post);
        if (nutzlast.fehler) {
          failures.push({ id: post.id, reason: nutzlast.fehler });
          continue;
        }
        const { text, hashtags } = nutzlast;

        const mediaUrl = post.image_url || post.media_url || null;
        const isVideo = !!post.video_url || (typeof mediaUrl === 'string' && /\.(mp4|mov|webm)/i.test(mediaUrl));
        if (isVideo) {
          failures.push({ id: post.id, reason: t('Video übersprungen', 'Video skipped') });
          continue;
        }

        try {
          const res = await axios.post(`${API}/live-posting/publish-direct`, {
            platform,
            text,
            hashtags,
            media_url: mediaUrl,
            media_type: 'image',
            title: nutzlast.title
          });
          if (res.data?.success && res.data?.simulated === false) {
            successCount++;
          } else {
            failures.push({
              id: post.id,
              reason: res.data?.user_message || res.data?.error || t('Unbekannter Fehler', 'Unknown error')
            });
          }
        } catch (err) {
          failures.push({
            id: post.id,
            reason: coerceError(err)
          });
        }
      }

      if (successCount > 0 && failures.length === 0) {
        toast.success(t(
          `${successCount} ${platformLabel}-Post${successCount === 1 ? '' : 's'} veröffentlicht!`,
          `${successCount} ${platformLabel} post${successCount === 1 ? '' : 's'} published!`,
        ));
      } else if (successCount > 0) {
        toast.success(t(
          `${successCount} veröffentlicht, ${failures.length} fehlgeschlagen. Erster Fehler: ${failures[0].reason}`,
          `${successCount} published, ${failures.length} failed. First error: ${failures[0].reason}`,
        ));
      } else {
        toast.error(t(
          `Veröffentlichung fehlgeschlagen: ${failures[0]?.reason || 'unbekannt'}`,
          `Publishing failed: ${failures[0]?.reason || 'unknown'}`,
        ));
      }
      fetchSocialStatus();
    } catch (error) {
      toast.error(t('Fehler: ', 'Error: ') + coerceError(error));
    } finally {
      setPostingPlatform(null);
    }
  };

  // Handler für Perplexity Trend-Idee zu Post
  const handleTrendIdeaToPost = async (idea) => {
    try {
      // Generiere Post aus Trend-Idee mit Brand Voice
      const response = await axios.post(`${API}/brand/optimize`, {
        content: idea.core_message,
        channel: 'instagram', // Default channel
        topic: idea.title
      });
      
      // Füge zum Kalender hinzu (simuliert)
      toast.success(`Post "${idea.title}" erstellt! (Brand Voice angewandt)`);
      fetchCalendar();
    } catch (error) {
      // Fallback: Zeige Info-Toast
      toast.info(`Idee "${idea.title}" bereit zur Verwendung`);
    }
  };

  // ── Kalender 2.0 Handler ─────────────────────────────────────────────────────

  const handleStatusChange = async (post, newStatus) => {
    if (post.status === newStatus) return;
    // Sicherung: 'scheduled' läuft NIE über die Quick-Status-Buttons — der Übergang in die
    // Auto-Post-Kette erfordert Datum+Zeit und geht ausschließlich über den Einplanen-Dialog.
    // 'needs_reschedule' ist ein SYSTEM-Status (nur vom Scheduler gesetzt) und nie manuell wählbar.
    if (newStatus === 'scheduled' || newStatus === 'needs_reschedule') return;
    try {
      await axios.patch(`${API}/monthly-plan/${post.id}`, { status: newStatus });
      fetchCalendar();
    } catch (err) {
      toast.error(t("Status konnte nicht geändert werden", "Status could not be changed"));
    }
  };

  // ── Einplanen (approved -> scheduled) ────────────────────────────────────────
  // Öffnet den Dialog mit einem Default-Zeitpunkt in der ZUKUNFT (morgen, 10:00).
  const handleOpenScheduleDialog = (post) => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 10, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const defaultDate = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;
    setScheduleError("");
    // Bei 'needs_reschedule' ist scheduled_date der verpasste (vergangene) Termin — den NICHT
    // vorbelegen, sonst startet der Dialog mit ungültiger Vergangenheit. Stattdessen Zukunfts-Default.
    const prefillDate = post.status === 'needs_reschedule' ? defaultDate : (post.scheduled_date || defaultDate);
    setScheduleFields({
      date: prefillDate,
      time: post.scheduled_time || "10:00",
    });
    setSchedulingPost(post);
  };

  const handleCloseScheduleDialog = () => {
    setSchedulingPost(null);
    setScheduleError("");
    setSchedulingSaving(false);
  };

  // Sendet GENAU EIN PATCH {status:'scheduled', scheduled_date, scheduled_time}.
  // JS-Vorprüfung (Zukunft) ist nur Komfort — bei Backend-400 wird der deutsche detail-Text gezeigt.
  const handleSubmitSchedule = async () => {
    if (!schedulingPost) return;
    const { date, time } = scheduleFields;
    if (!date || !time) {
      setScheduleError("Bitte Datum und Uhrzeit angeben.");
      return;
    }
    // Vergleich mit jetzt (lokale Zeit ~ Europe/Berlin des Nutzers). Backend prüft final in Europe/Berlin.
    const chosen = new Date(`${date}T${time}`);
    if (isNaN(chosen.getTime())) {
      setScheduleError("Datum oder Uhrzeit ist ungültig.");
      return;
    }
    if (chosen.getTime() <= Date.now()) {
      setScheduleError("Der geplante Zeitpunkt muss in der Zukunft liegen.");
      return;
    }
    setSchedulingSaving(true);
    setScheduleError("");
    try {
      await axios.patch(`${API}/monthly-plan/${schedulingPost.id}`, {
        status: "scheduled",
        scheduled_date: date,
        scheduled_time: time,
      });
      toast.success("Beitrag eingeplant");
      handleCloseScheduleDialog();
      fetchCalendar();
    } catch (err) {
      // Backend ist die Wahrheit: deutschen detail-Text NICHT verschlucken.
      const detail = err?.response?.data?.detail;
      setScheduleError(typeof detail === "string" && detail ? detail : coerceError(err, "Einplanen nicht möglich."));
      setSchedulingSaving(false);
    }
  };

  // ── Aus Planung nehmen (scheduled -> draft) ──────────────────────────────────
  // Sicherer Rückweg aus der Auto-Post-Kette.
  const handleUnschedule = async (post) => {
    try {
      await axios.patch(`${API}/monthly-plan/${post.id}`, { status: "draft" });
      toast.success("Beitrag aus der Planung genommen");
      fetchCalendar();
    } catch (err) {
      toast.error("Konnte nicht aus der Planung genommen werden: " + coerceError(err));
    }
  };

  const handleSaveNotes = async (postId, text) => {
    setSavingNotes(postId);
    try {
      await axios.patch(`${API}/monthly-plan/${postId}`, { notes: text });
      // Stille Speicherung — kein fetchCalendar() um UI nicht zu unterbrechen
    } catch (err) {
      toast.error("Notiz konnte nicht gespeichert werden");
    } finally {
      setSavingNotes(null);
    }
  };

  const handleSaveEdit = async (postId) => {
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/monthly-plan/${postId}`, {
        hook: editFields.hook,
        scheduled_time: editFields.time,
        platform: editFields.platform,
        content_type: editFields.content_type,
        pillar: editFields.pillar,
      });
      toast.success(t("Änderungen gespeichert", "Changes saved"));
      setEditingPostId(null);
      fetchCalendar();
    } catch (err) {
      toast.error(t("Fehler: ", "Error: ") + coerceError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Bild nachreichen an einem BESTEHENDEN Post ─────────────────────────────
  // POST /monthly-plan/{id}/image funktioniert an JEDEM Post, nicht nur bei neuen.
  // Damit entfällt die frühere Falschauskunft „Beitrag löschen und neu erstellen".
  const handleUploadImageToPost = async (postId, file) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(t("Nur JPG, PNG oder WebP möglich.", "Only JPG, PNG, or WebP files are allowed."));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t(
        `Das Bild ist ${(file.size / 1024 / 1024).toFixed(1)} MB groß — erlaubt sind 15 MB.`,
        `The image is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 15 MB.`,
      ));
      return;
    }
    setUploadingImageFor(postId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/monthly-plan/${postId}/image`, fd);
      toast.success(t("Bild hinzugefügt 🦊", "Image added 🦊"));
      fetchCalendar();
    } catch (err) {
      toast.error(t("Bild konnte nicht hochgeladen werden: ", "Could not upload image: ") + coerceError(err));
    } finally {
      setUploadingImageFor(null);
    }
  };

  // ── Video (Reel) nachreichen an einem BESTEHENDEN Post ─────────────────────
  // POST /monthly-plan/{id}/video — setzt video_url UNABHÄNGIG von image_url,
  // das Bild bleibt also erhalten (Kanäle ohne Video-Sendeweg weichen darauf aus).
  const handleUploadVideoToPost = async (postId, file) => {
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      toast.error(t("Nur MP4 oder MOV möglich.", "Only MP4 or MOV files are allowed."));
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error(t(
        `Das Video ist ${(file.size / 1024 / 1024).toFixed(0)} MB groß — erlaubt sind 200 MB.`,
        `The video is ${(file.size / 1024 / 1024).toFixed(0)} MB — the limit is 200 MB.`,
      ));
      return;
    }
    setUploadingVideoFor(postId);
    setVideoUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/monthly-plan/${postId}/video`, fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setVideoUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success(t("Video hinzugefügt 🦊", "Video added 🦊"));
      fetchCalendar();
    } catch (err) {
      toast.error(t("Video konnte nicht hochgeladen werden: ", "Could not upload video: ") + coerceError(err));
    } finally {
      setUploadingVideoFor(null);
      setVideoUploadProgress(0);
    }
  };

  // ── „Bilder neu erzeugen" — andere Layout-Variante der Karten anfordern ────
  // Der Endpunkt wird serverseitig noch gebaut (siehe open_issues). Bewusst KEINE
  // stille Attrappe: schlägt der Aufruf fehl, sieht der Kunde eine echte Meldung.
  const handleRegenerateCards = async (post) => {
    const postId = post?.id;
    if (!postId) return;
    setRegeneratingCardsFor(postId);
    try {
      const res = await axios.post(`${API}/monthly-plan/${postId}/regenerate-cards`, {
        variant: "auto",
      });
      if (res.data?.success) {
        toast.success("Neue Bildvariante erzeugt 🦊");
        fetchCalendar();
      } else {
        toast.error(res.data?.user_message || res.data?.error || "Bilder konnten nicht neu erzeugt werden.");
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(coerceError(err, "Dein Tarif erlaubt keine weiteren Bilder in diesem Monat."));
      } else {
        toast.error("Bilder konnten nicht neu erzeugt werden: " + coerceError(err));
      }
    } finally {
      setRegeneratingCardsFor(null);
    }
  };

  // Formular leeren — inklusive Bildauswahl. Der ObjectURL wird freigegeben,
  // sonst hält der Browser die Datei bis zum Reload im Speicher.
  const resetNewPostForm = useCallback(() => {
    setNewPostFields(EMPTY_NEW_POST_FIELDS);
    setNewPostFile(null);
    setNewPostFileError(null);
    setNewPostFilePreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
  }, []);

  const handleNewPostFileChange = (file) => {
    setNewPostFilePreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (!file) { setNewPostFile(null); setNewPostFileError(null); return; }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setNewPostFile(null);
      setNewPostFileError(t("Nur JPG, PNG oder WebP möglich.", "Only JPG, PNG, or WebP files are allowed."));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setNewPostFile(null);
      setNewPostFileError(t(
        `Das Bild ist ${(file.size / 1024 / 1024).toFixed(1)} MB groß — erlaubt sind 15 MB.`,
        `The image is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 15 MB.`,
      ));
      return;
    }
    setNewPostFileError(null);
    setNewPostFile(file);
    setNewPostFilePreview(URL.createObjectURL(file));
  };

  const handleCreateManualPost = async () => {
    if (!newPostFields.hook.trim()) { toast.error(t("Bitte einen Hook eingeben", "Please enter a hook")); return; }
    if (newPostFileError) { toast.error(newPostFileError); return; }
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
    setSavingNewPost(true);
    try {
      const res = await axios.post(`${API}/monthly-plan/manual-post`, {
        ...newPostFields,
        // 'time' bleibt aus Bestandsgründen im Body; scheduled_time ist der Feldname,
        // den das Backend seit jeher liest.
        scheduled_time: newPostFields.time,
        scheduled_date: dateStr,
      });

      // Bild erst NACH dem Anlegen hochladen — der Post existiert dann bereits und
      // geht bei einem Upload-Fehler nicht verloren.
      let imageFailed = false;
      if (newPostFile) {
        const newId = res.data?.post?.id;
        if (!newId) {
          imageFailed = true;
        } else {
          try {
            const fd = new FormData();
            fd.append("file", newPostFile);
            await axios.post(`${API}/monthly-plan/${newId}/image`, fd);
          } catch {
            imageFailed = true;
          }
        }
      }

      if (imageFailed) {
        // Ehrlicher Weg: Das Bearbeiten-Formular hat jetzt ein Bildfeld — der Kunde
        // kann das Bild jederzeit nachreichen, ohne den Beitrag zu löschen.
        toast.warning(t(
          "Der Beitrag wurde angelegt, aber ohne das Bild. Du kannst es jederzeit über „Bearbeiten“ bei diesem Beitrag nachreichen.",
          "The post was created, but without the image. You can add it anytime via \"Edit\" on this post.",
        ));
      } else {
        toast.success(t("Post erstellt!", "Post created!"));
      }
      setShowNewPostForm(false);
      resetNewPostForm();
      fetchCalendar();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(coerceError(err, t(
          "Dein Tarif erlaubt keine weiteren Beiträge in diesem Monat. Lösche einen Beitrag oder wechsle in einen größeren Tarif.",
          "Your plan doesn't allow any more posts this month. Delete a post or upgrade to a larger plan.",
        )));
      } else {
        toast.error(t("Fehler: ", "Error: ") + coerceError(err));
      }
    } finally {
      setSavingNewPost(false);
    }
  };

  // ── Vorschau eines vorbereiteten Posts (Klick auf Datum/Uhrzeit) ───────────
  // Nimmt IMMER das volle Post-Objekt aus postsThisMonth. Die Kalender-Fragmente
  // haben keinen Textkörper — eine Vorschau daraus wäre leer.
  const openPostPreview = useCallback((postOrId) => {
    const pid = typeof postOrId === "string" ? postOrId : postOrId?.id;
    const full = postsThisMonth.find(p => p.id === pid);
    if (!full) {
      toast.info("Für diesen Beitrag liegt kein vollständiger Inhalt vor — Vorschau nicht möglich.");
      return;
    }
    // Ohne 'status' ist der Veröffentlichen-Weg im Dialog zusätzlich hart gesperrt
    // (die Status-Prüfung dort schlägt fehl) — ein Klick aufs Datum kann also unter
    // keinen Umständen etwas live stellen.
    const { status, ...previewSafe } = full;
    setPreviewMode("preview");
    setPublishPreviewPost(previewSafe);
  }, [postsThisMonth]);

  // ── „+" im Panel: Formular für ein bestimmtes Datum öffnen ─────────────────
  const handleAddPostForDate = useCallback((dateStr) => {
    const parts = String(dateStr || "").split("-");
    if (parts.length < 3) return;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!d || m !== selectedMonth || y !== selectedYear) return;
    setSelectedDay(d);
    setEditingPostId(null);
    resetNewPostForm();
    setShowNewPostForm(true);
    setTimeout(() => {
      document.getElementById("kalender-bereich")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [selectedMonth, selectedYear, resetNewPostForm]);

  // ── Sekundäraktion „Plan neu erstellen" ───────────────────────────────────
  // Die Neuerzeugung löscht serverseitig ALLE Autopilot-Posts des Monats (delete_many)
  // — inklusive aller Kundenbearbeitungen. Deshalb hier eine ehrliche Rückfrage.
  const handleRegeneratePlan = useCallback(async () => {
    const monthLabel = `${monthNames[selectedMonth]} ${selectedYear}`;
    const ok = window.confirm(
      `Plan für ${monthLabel} neu erstellen?\n\n` +
      `Dabei werden alle automatisch erzeugten Beiträge dieses Monats gelöscht und ersetzt — ` +
      `samt deiner Änderungen daran (Texte, Uhrzeiten, Status, Notizen).\n` +
      `Selbst angelegte Beiträge bleiben erhalten.\n\n` +
      `Das lässt sich nicht rückgängig machen.`
    );
    if (!ok) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/autopilot/one-click`, {
        month: selectedMonth,
        year: selectedYear,
      });
      const count = res.data?.imported_count ?? res.data?.total_posts ?? 0;
      trackEvent("monthly_plan_generated", { posts_count: count, source: "regenerate" });
      toast.success(`${count} Posts für ${monthLabel} neu erstellt 🦊`);
      // Frisch laden UND ehrliche Aktivierungs-Zusammenfassung (scheduled vs. Ausnahmen).
      const posts = await fetchCalendar();
      showActivationSummary(posts);
    } catch (err) {
      toast.error("Der Plan konnte nicht neu erstellt werden: " + coerceError(err));
    } finally {
      setGenerating(false);
    }
  // monthNames ist eine im Render neu erzeugte Konstante — bewusst nicht in den Deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, fetchCalendar, showActivationSummary]);

  const handleExportCSV = async () => {
    try {
      const url = `${API}/monthly-plan/export?month=${selectedMonth}&year=${selectedYear}`;
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `monatsplan_${monthNames[selectedMonth]}${selectedYear}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("CSV exportiert");
    } catch (err) {
      toast.error("Export fehlgeschlagen");
    }
  };

  const fetchShareLinks = async () => {
    try {
      const res = await axios.get(`${API}/share/my-links?month=${selectedMonth}&year=${selectedYear}`);
      setShareLinks(res.data.links || []);
    } catch { /* still works if endpoint not available */ }
  };

  const handleCreateShareLink = async () => {
    setCreatingLink(true);
    try {
      const res = await axios.post(`${API}/share/create`, {
        month: selectedMonth,
        year: selectedYear,
        label: shareLabel.trim() || undefined,
        expires_in_days: 14,
      });
      toast.success("Freigabe-Link erstellt!");
      setShareLabel("");
      fetchShareLinks();
      // URL in Zwischenablage kopieren
      const url = `${window.location.origin}/share/${res.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Link in Zwischenablage kopiert");
    } catch (err) {
      toast.error("Link konnte nicht erstellt werden");
    } finally {
      setCreatingLink(false);
    }
  };

  const handleRevokeShareLink = async (token) => {
    if (!window.confirm("Diesen Link widerrufen? Er wird sofort ungültig.")) return;
    try {
      await axios.post(`${API}/share/revoke`, { token });
      toast.success("Link widerrufen");
      fetchShareLinks();
    } catch {
      toast.error("Widerrufen fehlgeschlagen");
    }
  };

  // ── Kalender 2.5 — Qualitätsprüfung ──────────────────────────────────────

  const handleRunAudit = async () => {
    // Qualitätsprüfung ist eine interne Systemfunktion — ohne QA-Oberfläche wird sie
    // vom Frontend gar nicht erst angestoßen (das System prüft selbst).
    if (!showQaUi) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/monthly-plan/quality-audit?month=${selectedMonth}&year=${selectedYear}`);
      setAuditData(res.data);
      if (res.data.has_data && res.data.summary?.needs_attention === 0) {
        toast.success("✅ Alle Posts sehen gut aus – kein Feinschliff nötig!");
      }
    } catch (err) {
      toast.error("Qualitätsprüfung fehlgeschlagen: " + coerceError(err));
    } finally {
      setAuditLoading(false);
    }
  };

  // Schnelles Lookup-Objekt: { postId → auditEntry } — für Kalender-Zellen & Detail-Panel
  const auditMap = {};
  if (auditData?.posts) {
    auditData.posts.forEach(p => { auditMap[p.post_id] = p; });
  }

  // ── Kalender 2.4 — Content-Varianten ──────────────────────────────────────

  const handleGenerateVariants = async (post) => {
    const pid = post.id;
    setVariantsMap(prev => ({ ...prev, [pid]: { items: [], loading: true, error: null } }));
    try {
      const res = await axios.post(`${API}/monthly-plan/${pid}/variations`, { count: 3, mode: "mixed" });
      if (res.data.success) {
        setVariantsMap(prev => ({ ...prev, [pid]: { items: res.data.variants, loading: false, error: null } }));
      } else {
        setVariantsMap(prev => ({ ...prev, [pid]: { items: [], loading: false, error: "Generierung fehlgeschlagen" } }));
      }
    } catch (err) {
      const msg = coerceError(err, "Fehler bei der Generierung");
      setVariantsMap(prev => ({ ...prev, [pid]: { items: [], loading: false, error: msg } }));
    }
  };

  const handleAdoptVariant = async (post, variant) => {
    const pid = post.id;
    setAdoptingVariant(pid);
    try {
      await axios.patch(`${API}/monthly-plan/${pid}`, {
        hook:         variant.hook,
        hook_full:    variant.hook,
        core_content: variant.text,
        text_content: variant.text,
      });
      toast.success(`✅ Variante „${variant.title}" übernommen`);
      // Varianten-Panel schließen + Kalender neu laden
      setVariantsMap(prev => ({ ...prev, [pid]: undefined }));
      fetchCalendar();
    } catch (err) {
      toast.error("Variante konnte nicht übernommen werden: " + coerceError(err));
    } finally {
      setAdoptingVariant(null);
    }
  };

  const handleCopyShareLink = async (token) => {
    const url = `${window.location.origin}/share/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert!");
    } catch {
      toast.error("Kopieren fehlgeschlagen — Link: " + url);
    }
  };

  const prevMonth = () => {
    setSelectedDay(null);
    setEditingPostId(null);
    setShowNewPostForm(false);
    setAuditData(null);
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };

  const nextMonth = () => {
    setSelectedDay(null);
    setEditingPostId(null);
    setShowNewPostForm(false);
    setAuditData(null);
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };

  // Pillar-Labels für Dropdown
  const PILLAR_OPTIONS = [
    { value: "", label: t("— kein Pillar —", "— no pillar —") },
    { value: "education", label: t("Mehrwert", "Value") },
    { value: "trust", label: t("Vertrauen", "Trust") },
    { value: "engagement", label: t("Interaktion", "Engagement") },
    { value: "offer", label: t("Angebote", "Offers") },
  ];

  const SOURCE_BADGE = {
    autopilot:       { label: "🤖 Autopilot",   cls: "bg-orange-100 text-orange-700" },
    manual:          { label: "✏️ Manuell",      cls: "bg-blue-100 text-blue-700" },
    strategy_expert: { label: "🧠 Strategie",   cls: "bg-violet-100 text-violet-700" },
    refresh_draft:   { label: "✨ Refresh",      cls: "bg-violet-100 text-violet-700" },
    legacy:          { label: "📄 Alt",          cls: "bg-stone-100 text-stone-500" },
  };

  const FORMAT_BADGE = {
    post:      { label: "Beitrag",   cls: "bg-stone-100 text-stone-600" },
    carousel:  { label: "Karussell", cls: "bg-cyan-100 text-cyan-700" },
    video:     { label: "Video",     cls: "bg-violet-100 text-violet-700" },
  };

  // Status-Konfiguration — verbale Labels + Farben für UI
  const STATUS_CONFIG = {
    draft:     { label: t("Entwurf", "Draft"),               cls: "bg-stone-100 text-stone-600",   dot: "bg-stone-300",   btnCls: "bg-stone-100 text-stone-600 hover:bg-stone-200"  },
    review:    { label: t("Zur Freigabe", "For approval"),   cls: "bg-amber-100 text-amber-700",   dot: "bg-amber-400",   btnCls: "bg-amber-100 text-amber-700 hover:bg-amber-200"  },
    approved:  { label: t("Freigegeben", "Approved"),        cls: "bg-green-100 text-green-700",   dot: "bg-green-400",   btnCls: "bg-green-100 text-green-700 hover:bg-green-200"  },
    done:      { label: t("Erledigt", "Done"),                cls: "bg-stone-100 text-stone-400",   dot: "bg-stone-200",   btnCls: "bg-stone-100 text-stone-400 hover:bg-stone-200"  },
    published: { label: t("Veröffentlicht", "Published"),    cls: "bg-green-100 text-green-700",   dot: "bg-green-500",   btnCls: "bg-green-100 text-green-700"                     },
    ready:     { label: t("Bereit", "Ready"),                 cls: "bg-blue-100 text-blue-700",     dot: "bg-blue-400",    btnCls: "bg-blue-100 text-blue-700 hover:bg-blue-200"     },
    scheduled: { label: t("Geplant", "Scheduled"),            cls: "bg-sky-100 text-sky-700",       dot: "bg-sky-400",     btnCls: "bg-sky-100 text-sky-700 hover:bg-sky-200"        },
    // System-Status: der Scheduler setzt abgelaufene 'scheduled'-Posts hierher (kein Auto-Post
    // eines vergangenen Termins). Auffällig (orange), aber nicht alarmistisch — Handlung = neu einplanen.
    needs_reschedule: { label: t("Termin überschritten", "Past due"), cls: "bg-orange-100 text-orange-700", dot: "bg-orange-400", btnCls: "bg-orange-100 text-orange-700 hover:bg-orange-200" },
    // Ausnahme nach Auto-Scheduling: Ziel-Kanal (noch) nicht verbunden/postfähig. Orange, kein Fehler —
    // der Beitrag wartet, Handlung = Kanal verbinden oder Beitrag umstellen.
    blocked_channel: { label: t("Kanal nicht verbunden", "Channel not connected"), cls: "bg-orange-100 text-orange-700", dot: "bg-orange-400", btnCls: "bg-orange-100 text-orange-700 hover:bg-orange-200" },
    // Ausnahme: Tarif pausiert → kein automatisches Posten. Ehrliches deutsches Label statt rohem String.
    paused_plan:     { label: t("Tarif pausiert", "Plan paused"),        cls: "bg-orange-100 text-orange-700", dot: "bg-orange-400", btnCls: "bg-orange-100 text-orange-700 hover:bg-orange-200" },
    failed:    { label: t("Fehlgeschlagen", "Failed"), cls: "bg-red-100 text-red-600",       dot: "bg-red-400",     btnCls: "bg-red-100 text-red-600"                         },
  };

  // Fallback, falls ein unbekannter Status ankommt — verhindert Absturz beim Zugriff auf .cls/.label/.dot.
  const statusMeta = (status) =>
    STATUS_CONFIG[status] || { label: status || t("Unbekannt", "Unknown"), cls: "bg-stone-100 text-stone-500", dot: "bg-stone-300", btnCls: "bg-stone-100 text-stone-500" };

  // Kanäle, die derzeit WIRKLICH automatisch live gehen (Owner-Freigabe 27.07.: nur LinkedIn + Bluesky).
  // Alles andere bleibt geplant, erscheint aber noch nicht automatisch — ehrlich kennzeichnen, nichts erfinden.
  const AUTO_PUBLISH_PLATFORMS = ["linkedin", "bluesky"];
  const platformAutoPublishes = (platform) =>
    AUTO_PUBLISH_PLATFORMS.includes(String(platform || "").toLowerCase().trim());

  // 'scheduled' gehört bewusst NICHT hier hinein — der Übergang in die Auto-Post-Kette
  // erfordert Datum+Zeit und läuft ausschließlich über den Einplanen-Dialog.
  // Die 4 Review-Flow-Stufen (für den Schnell-Wechsel im Panel)
  const REVIEW_FLOW = [
    { key: "draft",    label: t("Entwurf", "Draft") },
    { key: "review",   label: t("Zur Freigabe", "For approval") },
    { key: "approved", label: t("Freigegeben", "Approved") },
    { key: "done",     label: t("Erledigt", "Done") },
  ];

  const formatNotesDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  // Verpassten Termin dezent darstellen ("DD.MM. HH:MM"). Robust ggü. fehlenden Feldern.
  const formatMissedSchedule = (date, time) => {
    if (!date) return "";
    const parts = String(date).split("-"); // YYYY-MM-DD
    const dm = parts.length === 3 ? `${parts[2]}.${parts[1]}.` : String(date);
    const timePart = time ? ` ${String(time)}` : "";
    return `${dm}${timePart}`;
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
  const monthNames = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const calendarDays = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
    calendarDays.push(null);
  }
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const getPostsForDay = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData[dateStr] || [];
  };

  // ── B) Video-Badge-Konfiguration ─────────────────────────────────────────
  // Zentraler Helper statt verschachtelter Ternäre — erleichtert neue Status-Zweige.
  // Gibt ein config-Objekt zurück; der Render-Block entscheidet über kind.
  const getVideoActionConfig = (post) => {
    const s    = post.video_status;
    const url  = post.video_url;
    const navState = {
      post_id:    post.id || '',
      topic:      post.topic || post.title || post.hook || '',
      text:       post.content || post.text_content || post.core_content || '',
      channel:    post.channel || post.platform || 'instagram',
      image_idea: post.image_idea || '',
      video_type: null,
    };

    // Simuliertes Video (video_url gesetzt, aber Status = simulated)
    if (url && s === 'simulated') {
      return { kind: 'link', label: '🎭 Simuliert', className: 'bg-violet-50 text-violet-700 hover:bg-violet-100', href: url, title: 'Simuliertes Video ansehen' };
    }
    // Echtes Video vorhanden
    if (url) {
      return { kind: 'link+recreate', label: '🎬 Video', className: 'bg-green-50 text-green-700 hover:bg-green-100', href: url, title: 'Video ansehen', navState };
    }
    // Läuft gerade (inkl. generatingVideo-Lokal-State)
    if (s === 'generating' || s === 'queued' || generatingVideo === post.id) {
      return { kind: 'loading', label: 'Video läuft…' };
    }
    // Tageslimit erreicht
    if (s === 'rate_limited') {
      return { kind: 'info', label: 'Limit erreicht', className: 'bg-amber-50 text-amber-700', title: 'Tageslimit für Videos erreicht — morgen erneut versuchen' };
    }
    // Fehler / Timeout / Fehlschlag → Retry
    if (s === 'failed' || s === 'timeout' || s === 'error') {
      return { kind: 'retry', label: 'Erneut', className: 'bg-red-50 text-red-600 hover:bg-red-100', title: 'Video erneut erstellen', navState };
    }
    // Noch kein Video
    return { kind: 'start', label: 'Video erstellen', className: 'bg-purple-100 text-purple-700 hover:bg-purple-200', navState };
  };

  return (
    <>
    <div data-testid="monthly-plan-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">Monatsplan</h1>
          <p className="text-stone-500 mt-1">Automatisch geplant — du kannst jeden Entwurf anpassen.</p>
        </div>
      </div>

      {/* ── 1-Klick-Monatsplan: nur solange für DIESEN Monat noch kein Plan existiert.
          Sobald Posts für den Monat da sind, verschwindet das obere Feld komplett —
          der fertige Plan (PreparedPostsPanel) übernimmt die Bühne. */}
      {!hasPlanForMonth && (
        <OneClickPlanButton
          month={selectedMonth}
          year={selectedYear}
          onCreated={handlePlanActivated}
        />
      )}

      {/* ── Der fertige Monatsplan: steht ganz oben, damit sofort sichtbar ist,
          welche Beiträge bereits erstellt wurden. */}
      {hasPlanForMonth && (
        <PreparedPostsPanel
          posts={postsThisMonth}
          month={selectedMonth}
          year={selectedYear}
          onPreview={openPostPreview}
          onAddPost={handleAddPostForDate}
          onRegenerate={handleRegeneratePlan}
        />
      )}

      {/* Calendar View */}
      <div id="kalender-bereich" className="scroll-mt-32 bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-3">
          {/* Month navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
              title="Vorheriger Monat"
            >
              <ChevronLeft size={16} />
            </button>
            <h2 className="font-heading text-xl font-bold min-w-[180px] text-center">
              {monthNames[selectedMonth]} {selectedYear}
            </h2>
            <button
              onClick={nextMonth}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
              title="Nächster Monat"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          {/* Legend + export */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-xs text-stone-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-300 inline-block" /> Autopilot</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-300 inline-block" /> Manuell</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-stone-200 inline-block" /> Erledigt</span>
            </div>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors"
              title="Monatsplan als CSV exportieren"
            >
              <Download size={13} />
              CSV
            </button>
            {/* Qualitätsprüfungs-Button — interne Systemfunktion, für Kunden unsichtbar
                (siehe QA_UI_ENABLED in config/features.js). */}
            {showQaUi && (
            <button
              data-tour="tour-audit-btn"
              onClick={handleRunAudit}
              disabled={auditLoading}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                auditData
                  ? auditData.summary?.needs_attention > 0
                    ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                    : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  : 'bg-stone-100 text-stone-600 border-stone-200 hover:bg-stone-200'
              }`}
              title="Alle Posts des Monats auf Qualität prüfen"
            >
              {auditLoading
                ? <Loader2 size={13} className="animate-spin" />
                : auditData
                  ? auditData.summary?.needs_attention > 0
                    ? <AlertTriangle size={13} />
                    : <ShieldCheck size={13} />
                  : <ShieldCheck size={13} />
              }
              {auditLoading ? 'Prüfe…' : 'Monat prüfen'}
              {auditData?.summary?.needs_attention > 0 && (
                <span className="ml-0.5 font-bold">{auditData.summary.needs_attention}</span>
              )}
            </button>
            )}
            {/* Freigabe-Link-Button */}
            <button
              onClick={() => {
                const opening = !sharePanelOpen;
                setSharePanelOpen(opening);
                if (opening) { fetchShareLinks(); fetchActivity(); }
              }}
              className="relative flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              title="Externen Freigabe-Link für Kunden erstellen"
            >
              <Share2 size={13} />
              Freigabe-Link
              {unreadActivity > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadActivity > 9 ? "9+" : unreadActivity}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ── Freigabe-Link-Panel ── */}
        {sharePanelOpen && (
          <div className="border-b border-amber-100 bg-amber-50/60 px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Share2 size={14} className="text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Freigabe-Link für Kunden</span>
              <span className="text-xs text-amber-500 ml-1">— gültig 14 Tage, kein Login nötig</span>
            </div>
            {/* Neuen Link erstellen */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <input
                type="text"
                placeholder={`Bezeichnung (z. B. „Kunde Mustermann")`}
                value={shareLabel}
                onChange={(e) => setShareLabel(e.target.value)}
                className="flex-1 min-w-[200px] text-sm border border-amber-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <button
                onClick={handleCreateShareLink}
                disabled={creatingLink}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                {creatingLink ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Link erstellen &amp; kopieren
              </button>
            </div>
            {/* Bestehende Links */}
            {shareLinks.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Aktive Links</p>
                {shareLinks.map(lnk => {
                  const isExpired = lnk.expired || !lnk.active;
                  const expiresStr = lnk.expires_at
                    ? new Date(lnk.expires_at).toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" })
                    : "";
                  return (
                    <div key={lnk.token} className={`flex items-center gap-2 flex-wrap p-2.5 rounded-lg border text-xs ${isExpired ? 'bg-stone-50 border-stone-200 opacity-60' : 'bg-white border-amber-200'}`}>
                      <span className="flex-1 font-medium text-stone-700 min-w-0 truncate">{lnk.label}</span>
                      {expiresStr && <span className="text-stone-400 flex-shrink-0">bis {expiresStr}</span>}
                      {lnk.action_count > 0 && <span className="text-green-600 flex-shrink-0">{lnk.action_count} Aktionen</span>}
                      {isExpired
                        ? <span className="text-stone-400">abgelaufen</span>
                        : (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleCopyShareLink(lnk.token)}
                              className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors"
                            >
                              Kopieren
                            </button>
                            <button
                              onClick={() => handleRevokeShareLink(lnk.token)}
                              className="px-2 py-0.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition-colors"
                            >
                              Widerrufen
                            </button>
                          </div>
                        )
                      }
                    </div>
                  );
                })}
              </div>
            )}
            {shareLinks.length === 0 && (
              <p className="text-xs text-amber-500">Noch keine Links für diesen Monat erstellt.</p>
            )}

            {/* ── Aktivitäts-Feed ── */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">
                  Neue Freigaben &amp; Kommentare
                </p>
                {unreadActivity > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[10px] text-amber-600 hover:text-amber-800 underline underline-offset-2 transition-colors"
                  >
                    Alle als gelesen markieren
                  </button>
                )}
              </div>

              {loadingActivity && (
                <div className="flex items-center gap-2 text-xs text-amber-500 py-2">
                  <Loader2 size={11} className="animate-spin" />
                  Lade Aktivitäten…
                </div>
              )}

              {!loadingActivity && activityItems.length === 0 && (
                <p className="text-xs text-amber-400 py-1">
                  Noch keine Aktivitäten. Sobald Kunden freigeben oder kommentieren, siehst du es hier.
                </p>
              )}

              {!loadingActivity && activityItems.length > 0 && (
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {activityItems.map((item, idx) => {
                    const isApproval = item.type === "approved_post";
                    const canJump = item.post_date &&
                      parseInt(item.post_date.split("-")[1], 10) === selectedMonth &&
                      parseInt(item.post_date.split("-")[0], 10) === selectedYear;
                    return (
                      <div
                        key={idx}
                        className={`flex items-start gap-2 p-2 rounded-lg border text-xs transition-colors ${
                          item.read
                            ? "bg-white border-stone-100 text-stone-500"
                            : isApproval
                              ? "bg-green-50 border-green-200 text-stone-700"
                              : "bg-amber-50/80 border-amber-200 text-stone-700"
                        }`}
                      >
                        {/* Icon */}
                        <span className="flex-shrink-0 mt-0.5 text-base leading-none">
                          {isApproval ? "✅" : "💬"}
                        </span>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium leading-snug truncate">
                            {item.message}
                            {item.post_hook && (
                              <span className="font-normal text-stone-400"> — „{item.post_hook}"</span>
                            )}
                          </p>
                          <p className="text-[10px] text-stone-400 mt-0.5">{formatActivityTime(item.created_at)}</p>
                        </div>
                        {/* Jump-to-post */}
                        {canJump && (
                          <button
                            onClick={() => handleJumpToPost(item.post_date)}
                            className="flex-shrink-0 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors whitespace-nowrap"
                            title="Zum Post springen"
                          >
                            → Tag
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Qualitäts-Audit-Ergebnisse (nur interne Ansicht) ── */}
        {showQaUi && auditData && auditData.has_data && (
          <div className={`border-b px-5 py-4 ${
            auditData.summary.needs_attention > 0
              ? 'bg-red-50/50 border-red-100'
              : 'bg-green-50/50 border-green-100'
          }`}>
            {/* Summary-Zeile */}
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                {auditData.summary.needs_attention > 0
                  ? <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                  : <ShieldCheck size={14} className="text-green-600 flex-shrink-0" />
                }
                <span className={`text-sm font-semibold ${
                  auditData.summary.needs_attention > 0 ? 'text-red-700' : 'text-green-700'
                }`}>
                  {auditData.summary.needs_attention > 0
                    ? `${auditData.summary.needs_attention} von ${auditData.summary.total_checked} Posts brauchen noch Feinschliff`
                    : `Alle ${auditData.summary.total_checked} Posts sehen gut aus`
                  }
                </span>
              </div>
              <div className="flex items-center gap-2">
                {auditData.summary.top_issue && (
                  <span className="text-xs text-stone-500 italic hidden sm:inline">
                    Häufigstes Thema: „{auditData.summary.top_issue}"
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {auditData.summary.needs_attention > 0 && (
                    <button
                      data-tour="tour-batch-btn"
                      onClick={() => setBatchOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                      title="Alle schwachen Posts nacheinander verbessern"
                    >
                      <Sparkles size={11} />
                      Alle verbessern
                    </button>
                  )}
                  <button
                    onClick={() => setAuditData(null)}
                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                  >
                    Schließen
                  </button>
                </div>
              </div>
            </div>

            {/* Flagged Posts Liste */}
            {auditData.posts.length > 0 && (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {auditData.posts.map(item => {
                  const day = item.scheduled_date
                    ? parseInt(item.scheduled_date.split("-")[2], 10)
                    : null;
                  return (
                    <div
                      key={item.post_id}
                      className={`flex items-start gap-2.5 p-2.5 rounded-lg border text-xs ${
                        item.quality === "weak"
                          ? "bg-white border-red-200"
                          : "bg-white border-amber-200"
                      }`}
                    >
                      {/* Qualitäts-Icon */}
                      <span className={`flex-shrink-0 mt-0.5 font-bold ${
                        item.quality === "weak" ? "text-red-500" : "text-amber-500"
                      }`}>
                        {item.quality === "weak" ? "⚠" : "○"}
                      </span>
                      {/* Post-Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            item.quality === "weak"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {item.short_label}
                          </span>
                          <span className="text-stone-400 text-[10px] capitalize">{item.platform}</span>
                          {item.scheduled_date && (
                            <span className="text-stone-400 text-[10px]">{item.scheduled_date}</span>
                          )}
                        </div>
                        {item.hook_preview && (
                          <p className="text-stone-600 truncate">{item.hook_preview}</p>
                        )}
                        <p className="text-stone-400 text-[10px] mt-0.5">
                          {item.reasons.join(" · ")}
                        </p>
                      </div>
                      {/* Aktion: zum Tag springen + Varianten */}
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        {day && (
                          <button
                            onClick={() => handleJumpToPost(item.scheduled_date)}
                            className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded hover:bg-stone-200 transition-colors whitespace-nowrap"
                          >
                            → Tag
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            // Post-Objekt aus calendarData heraussuchen und Varianten generieren
                            const allPosts = Object.values(calendarData).flat();
                            const found = allPosts.find(p => p.id === item.post_id);
                            if (found) {
                              handleJumpToPost(item.scheduled_date);
                              setTimeout(() => handleGenerateVariants(found), 300);
                            }
                          }}
                          className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded hover:bg-violet-200 transition-colors whitespace-nowrap"
                        >
                          ✦ Verbessern
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
          </div>
        ) : (
          <>
            {/* Calendar Header */}
            <div className="grid grid-cols-7 border-b border-stone-200">
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-stone-500 bg-stone-50">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, index) => {
                const posts = day ? getPostsForDay(day) : [];
                const isSelected = selectedDay === day;
                const donePosts = posts.filter(p => p.status === "done").length;

                return (
                  <div
                    key={index}
                    onClick={() => { if (!day) return; setSelectedDay(isSelected ? null : day); if (isSelected) { setShowNewPostForm(false); setEditingPostId(null); } }}
                    className={`min-h-[100px] p-2 border-r border-b border-stone-100 cursor-pointer transition-colors relative group ${
                      day ? 'hover:bg-stone-50' : 'bg-stone-50/60'
                    } ${isSelected ? 'bg-fox-fire/5 ring-2 ring-fox-fire ring-inset' : ''}`}
                  >
                    {day && (
                      <>
                        {/* Day number + add button */}
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm ${posts.length > 0 ? 'text-stone-900' : 'text-stone-400'}`}>{day}</span>
                          {/* Dauerhaft sichtbar und mindestens 24x24 px — auf Touch-Geräten
                              gibt es kein Hover, ein 4x4-px-Ziel wäre unbedienbar. */}
                          <button
                            data-testid={`add-post-day-${day}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDay(day);
                              setEditingPostId(null);
                              resetNewPostForm();
                              setShowNewPostForm(true);
                            }}
                            className="opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity w-6 h-6 bg-fox-fire text-white rounded-lg flex items-center justify-center hover:bg-orange-600"
                            title="Eigenen Post hinzufügen"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                        {/* Post mini-badges */}
                        <div className="space-y-0.5">
                          {posts.slice(0, 3).map((post, i) => {
                            const isDone = post.status === "done" || post.status === "published";
                            const cellCls = isDone
                              ? "bg-stone-100 text-stone-400"
                              : post.source === "foerderfuchs_bonus"
                              ? "bg-violet-100 text-violet-700"
                              : post.source === "autopilot"
                              ? "bg-orange-100 text-orange-700"
                              : post.source === "manual"
                              ? "bg-blue-100 text-blue-700"
                              : themeColors[post.theme] || "bg-stone-100 text-stone-600";
                            // Status-Dot für Review/Freigabe (ergänzend zur Quell-Farbe)
                            const statusDot = !isDone && post.status === "review"
                              ? "bg-amber-400"
                              : !isDone && post.status === "approved"
                              ? "bg-green-400"
                              : !isDone && post.status === "needs_reschedule"
                              ? "bg-orange-400"
                              : null;
                            // Qualitäts-Marker aus Audit — nur in der internen Ansicht
                            const auditEntry = showQaUi ? auditMap[post.id] : null;
                            const qualityDot = !isDone && auditEntry
                              ? auditEntry.quality === "weak"  ? "bg-red-400"
                              : auditEntry.quality === "fair"  ? "bg-amber-300"
                              : null
                              : null;
                            // Video-Status-Dot — nur Inhaber (Video ist kein Kundenprodukt)
                            const videoDot = !isMaster
                              ? null
                              : !isDone && post.video_status
                              ? (post.video_status === 'ready' || post.video_status === 'completed' || post.video_url)
                                ? "bg-green-500"
                                : (post.video_status === 'generating' || post.video_status === 'queued')
                                  ? "bg-blue-400"
                                  : post.video_status === 'failed'
                                    ? "bg-red-400"
                                    : null
                              : (post.video_url && !isDone) ? "bg-green-500" : null;
                            return (
                              <div
                                key={i}
                                className={`text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-0.5 ${cellCls} ${isDone ? 'line-through' : ''}`}
                                title={`${post.time} — ${post.hook_full || post.hook}${post.status === 'review' ? ' · Zur Freigabe' : post.status === 'approved' ? ' · Freigegeben' : post.status === 'needs_reschedule' ? ' · Termin überschritten – bitte neu einplanen' : ''}${auditEntry ? ' · ' + auditEntry.short_label : ''}${isMaster && post.video_status ? ' · Video: ' + post.video_status : ''}`}
                              >
                                {isDone ? <span className="flex-shrink-0">✓</span> : null}
                                {post.source === "foerderfuchs_bonus" && !isDone
                                  ? <span className="flex-shrink-0" title="Förderfuchs-Bonus">🦊</span>
                                  : null}
                                <span className="truncate">{post.time} {post.content_type || post.theme?.substring(0, 3)}</span>
                                {/* Status + Qualitäts-Dots + Video-Dot nebeneinander */}
                                <span className="flex-shrink-0 ml-auto flex items-center gap-0.5">
                                  {statusDot && <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />}
                                  {qualityDot && <span className={`w-1.5 h-1.5 rounded-full ${qualityDot}`} title={auditEntry?.short_label} />}
                                  {videoDot && <span className={`w-1.5 h-1.5 rounded-full ${videoDot}`} title={`Video: ${post.video_status || 'bereit'}`} />}
                                </span>
                              </div>
                            );
                          })}
                          {posts.length > 3 && (
                            <div className="text-[10px] text-stone-400 px-1">
                              +{posts.length - 3} mehr
                            </div>
                          )}
                        </div>
                        {/* Done count badge */}
                        {donePosts > 0 && donePosts === posts.length && (
                          <div className="absolute bottom-1 right-1 text-[9px] text-green-600 font-semibold">✓ alle</div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Selected Day Detail — Kalender 2.0 */}
      {selectedDay && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden animate-fade-in">
          {/* Panel header */}
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between bg-stone-50">
            <h3 className="font-heading text-lg font-bold text-stone-900">
              {selectedDay}. {monthNames[selectedMonth]} {selectedYear}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setEditingPostId(null); resetNewPostForm(); setShowNewPostForm(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-fox-fire text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus size={12} /> Manueller Post
              </button>
              <button onClick={() => { setSelectedDay(null); setShowNewPostForm(false); setEditingPostId(null); }} className="text-stone-400 hover:text-stone-600">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* New post form */}
          {showNewPostForm && (
            <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">{t("Neuen Post erstellen", "Create new post")}</h4>
              <textarea
                rows={2}
                placeholder={t("Hook / Titel des Posts…", "Hook / post title…")}
                value={newPostFields.hook}
                onChange={(e) => setNewPostFields({ ...newPostFields, hook: e.target.value })}
                className="w-full text-sm border border-blue-200 rounded-lg p-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none mb-3"
              />
              <textarea
                rows={4}
                data-testid="new-post-core-content"
                placeholder={t("Beitragstext — was soll im Post stehen?", "Post text — what should the post say?")}
                value={newPostFields.core_content}
                onChange={(e) => setNewPostFields({ ...newPostFields, core_content: e.target.value })}
                className="w-full text-sm border border-blue-200 rounded-lg p-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none mb-3"
              />
              {/* Eigenes Bild — wird erst nach dem Anlegen des Posts hochgeladen */}
              <div className="mb-3">
                <label className="text-xs text-blue-700 block mb-1">{t("Bild (optional)", "Image (optional)")}</label>
                <input
                  type="file"
                  ref={newPostFileInputRef}
                  data-testid="new-post-image-input"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleNewPostFileChange(e.target.files?.[0] || null)}
                  className="w-full text-xs text-stone-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                />
                <p className="text-[10px] text-blue-500 mt-1">{t("JPG, PNG oder WebP — maximal 15 MB.", "JPG, PNG, or WebP — maximum 15 MB.")}</p>
                {newPostFileError && (
                  <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-2 py-1 mt-1.5">
                    {newPostFileError}
                  </p>
                )}
                {newPostFilePreview && (
                  <div className="mt-2 flex items-center gap-2">
                    <img
                      src={newPostFilePreview}
                      alt={t("Bildvorschau", "Image preview")}
                      className="w-20 h-20 object-cover rounded-lg border border-blue-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // Auswahl auch im Input zurücksetzen, sonst löst dieselbe Datei
                        // beim erneuten Wählen kein change-Event mehr aus.
                        if (newPostFileInputRef.current) newPostFileInputRef.current.value = "";
                        handleNewPostFileChange(null);
                      }}
                      className="text-xs text-stone-500 hover:text-stone-700 transition-colors"
                    >
                      {t("Bild entfernen", "Remove image")}
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div>
                  <label className="text-xs text-blue-700 block mb-1">{t("Uhrzeit", "Time")}</label>
                  <input
                    type="time"
                    value={newPostFields.time}
                    onChange={(e) => setNewPostFields({ ...newPostFields, time: e.target.value })}
                    className="w-full text-sm border border-blue-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                <div>
                  <label className="text-xs text-blue-700 block mb-1">{t("Plattform", "Platform")}</label>
                  <select
                    value={newPostFields.platform}
                    onChange={(e) => setNewPostFields({ ...newPostFields, platform: e.target.value })}
                    className="w-full text-sm border border-blue-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="tiktok">TikTok</option>
                    <option value="google_business">Google Business</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-blue-700 block mb-1">Format</label>
                  <select
                    value={newPostFields.content_type}
                    onChange={(e) => setNewPostFields({ ...newPostFields, content_type: e.target.value })}
                    className="w-full text-sm border border-blue-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="post">{t("Beitrag", "Post")}</option>
                    <option value="carousel">{t("Karussell", "Carousel")}</option>
                    {/* „Video" entfernt: es gibt für Kunden keinen Weg, daraus ein
                        Video zu machen — das Format wäre ein leeres Versprechen. */}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-blue-700 block mb-1">Pillar</label>
                  <select
                    value={newPostFields.pillar}
                    onChange={(e) => setNewPostFields({ ...newPostFields, pillar: e.target.value })}
                    className="w-full text-sm border border-blue-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    {PILLAR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateManualPost}
                  disabled={savingNewPost}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {savingNewPost ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  {t("Erstellen", "Create")}
                </button>
                <button
                  onClick={() => { setShowNewPostForm(false); resetNewPostForm(); }}
                  className="px-4 py-2 text-sm text-stone-500 hover:text-stone-700 transition-colors"
                >
                  {t("Abbrechen", "Cancel")}
                </button>
              </div>
            </div>
          )}

          {/* Sammelzeile Fachfreigabe — zählt über den GANZEN Plan, nicht nur den
              gewählten Tag. Ohne sie müsste der Kunde den Rückstand Tag für Tag
              entdecken; am 21.08.2026 waren es 50 Beiträge auf einmal. */}
          {wartendeFachfreigaben > 0 && (
            <div
              className="mx-6 mb-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3"
              data-testid="fachfreigabe-sammelzeile"
            >
              <p className="text-sm font-semibold text-purple-900">
                {wartendeFachfreigaben === 1
                  ? "1 Beitrag in diesem Monat wartet auf deine fachliche Freigabe"
                  : `${wartendeFachfreigaben} Beiträge in diesem Monat warten auf deine fachliche Freigabe`}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-purple-800">
                Sie enthalten sicherheitskritische Handlungsanweisungen. Solche Beiträge
                gehen bewusst nicht automatisch live, bevor ein fachkundiger Mensch sie
                gelesen und verantwortet hat — deshalb bleiben sie als Entwurf stehen.
                Die betroffenen Beiträge sind unten mit „Fachliche Freigabe nötig" markiert.
              </p>
            </div>
          )}

          {/* Post list */}
          <div className="divide-y divide-stone-100 px-6 py-2">
            {getPostsForDay(selectedDay).map((post, index) => {
              const isEditing = editingPostId === post.id;
              const isDone    = post.status === "done";
              // Einmal entscheiden, mehrfach anzeigen — die Funktion ist rein, aber
              // sechs Aufrufe pro Beitrag und Render sind keine Absicht.
              const fachfreigabe = fachfreigabeModell(post);
              // Vorschau nur anbieten, wenn der volle Beitrag im Monats-Array liegt
              // (reine Kalender-Fragmente hätten in der Vorschau leeren Inhalt).
              const hasFullPost = postsThisMonth.some(p => p.id === post.id);
              const srcBadge  = SOURCE_BADGE[post.source] || SOURCE_BADGE.legacy;
              const fmtBadge  = FORMAT_BADGE[post.content_type] || FORMAT_BADGE.post;

              return (
                <div key={post.id || index} className={`py-4 ${isDone ? 'opacity-60' : ''}`}>
                  {isEditing ? (
                    /* ── Inline Edit Form ── */
                    <div className="space-y-3">
                      <textarea
                        rows={3}
                        value={editFields.hook}
                        onChange={(e) => setEditFields({ ...editFields, hook: e.target.value })}
                        className="w-full text-sm border border-stone-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-fox-fire resize-none"
                      />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                          <label className="text-xs text-stone-500 block mb-1">{t("Uhrzeit", "Time")}</label>
                          <input
                            type="time"
                            value={editFields.time}
                            onChange={(e) => setEditFields({ ...editFields, time: e.target.value })}
                            className="w-full text-sm border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-fox-fire"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500 block mb-1">{t("Plattform", "Platform")}</label>
                          <select
                            value={editFields.platform}
                            onChange={(e) => setEditFields({ ...editFields, platform: e.target.value })}
                            className="w-full text-sm border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-fox-fire"
                          >
                            <option value="instagram">Instagram</option>
                            <option value="facebook">Facebook</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="tiktok">TikTok</option>
                            <option value="google_business">Google Business</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-stone-500 block mb-1">Format</label>
                          <select
                            value={editFields.content_type}
                            onChange={(e) => setEditFields({ ...editFields, content_type: e.target.value })}
                            className="w-full text-sm border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-fox-fire"
                          >
                            <option value="post">{t("Beitrag", "Post")}</option>
                            <option value="carousel">{t("Karussell", "Carousel")}</option>
                            <option value="video">Video</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-stone-500 block mb-1">Pillar</label>
                          <select
                            value={editFields.pillar}
                            onChange={(e) => setEditFields({ ...editFields, pillar: e.target.value })}
                            className="w-full text-sm border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-fox-fire"
                          >
                            {PILLAR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* ── Bild nachreichen / ersetzen ──────────────────────────
                          Der Upload läuft an JEDEM bestehenden Post (POST …/image).
                          Kein Löschen-und-neu-Anlegen mehr nötig. */}
                      <div className="pt-1">
                        <label className="text-xs text-stone-500 block mb-1">
                          {(post.card_urls?.length || post.image_url) ? t("Bild ersetzen", "Replace image") : t("Bild hinzufügen", "Add image")} (optional)
                        </label>
                        <div className="flex items-center gap-3">
                          {(post.card_urls?.[0] || post.image_url) && (
                            <img
                              src={mediaSrc(post.card_urls?.[0] || post.image_url)}
                              alt={t("Aktuelles Beitragsbild", "Current post image")}
                              className="w-14 h-14 rounded-lg object-cover border border-stone-200 flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          )}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            data-testid={`edit-post-image-input-${post.id}`}
                            disabled={uploadingImageFor === post.id}
                            onChange={(e) => {
                              const f = e.target.files?.[0] || null;
                              handleUploadImageToPost(post.id, f);
                              // Auswahl zurücksetzen, damit dieselbe Datei erneut wählbar bleibt.
                              e.target.value = "";
                            }}
                            className="w-full text-xs text-stone-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200 disabled:opacity-50"
                          />
                          {uploadingImageFor === post.id && (
                            <Loader2 size={14} className="animate-spin text-fox-fire flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-[10px] text-stone-400 mt-1">{t("JPG, PNG oder WebP — maximal 15 MB.", "JPG, PNG, or WebP — maximum 15 MB.")}</p>
                      </div>

                      {/* ── Video (Reel) nachreichen / ersetzen ─────────────────
                          Unabhängig vom Bild oben: Kanäle mit eigenem Video-Sendeweg
                          (Instagram Reel, Facebook, TikTok) nutzen das Video, Kanäle
                          ohne Video-Sendeweg weichen automatisch auf das Bild aus. */}
                      <div className="pt-1">
                        <label className="text-xs text-stone-500 block mb-1">
                          {post.video_url ? t("Video ersetzen", "Replace video") : t("Video (Reel) hinzufügen", "Add video (reel)")} (optional)
                          {editFields.content_type === "video" && (
                            <span className="ml-1.5 text-[10px] text-fox-fire font-medium">— {t("bevorzugtes Medium für dieses Format", "preferred media for this format")}</span>
                          )}
                        </label>
                        <div className="flex items-center gap-3">
                          {post.video_url && (
                            <video
                              src={mediaSrc(post.video_url)}
                              className="w-14 h-14 rounded-lg object-cover border border-stone-200 flex-shrink-0"
                              muted
                            />
                          )}
                          <input
                            type="file"
                            accept="video/mp4,video/quicktime"
                            data-testid={`edit-post-video-input-${post.id}`}
                            disabled={uploadingVideoFor === post.id}
                            onChange={(e) => {
                              const f = e.target.files?.[0] || null;
                              handleUploadVideoToPost(post.id, f);
                              e.target.value = "";
                            }}
                            className="w-full text-xs text-stone-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-stone-100 file:text-stone-700 hover:file:bg-stone-200 disabled:opacity-50"
                          />
                          {uploadingVideoFor === post.id && (
                            <Loader2 size={14} className="animate-spin text-fox-fire flex-shrink-0" />
                          )}
                        </div>
                        {uploadingVideoFor === post.id && (
                          <div className="w-full h-1.5 bg-stone-100 rounded-full mt-1.5 overflow-hidden">
                            <div
                              className="h-full bg-fox-fire transition-all"
                              style={{ width: `${videoUploadProgress}%` }}
                            />
                          </div>
                        )}
                        <p className="text-[10px] text-stone-400 mt-1">{t("MP4 oder MOV — maximal 200 MB.", "MP4 or MOV — maximum 200 MB.")}</p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(post.id)}
                          disabled={savingEdit}
                          className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-fox-fire text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
                        >
                          {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                          {t("Speichern", "Save")}
                        </button>
                        <button
                          onClick={() => setEditingPostId(null)}
                          className="px-4 py-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
                        >
                          {t("Abbrechen", "Cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Normal view ── */
                    <>
                      {/* ── Badges-Zeile: Uhrzeit / Quelle / Format / Plattform / Sonderbadges ── */}
                      <div className="flex items-center flex-wrap gap-1.5 mb-2">
                        {/* Datum + Uhrzeit öffnen die Vorschau genau dieses Beitrags.
                            Reine Ansicht — von hier führt kein Weg zum Veröffentlichen. */}
                        {hasFullPost ? (
                          <button
                            type="button"
                            data-testid={`preview-date-${post.id}`}
                            onClick={(e) => { e.stopPropagation(); openPostPreview(post.id); }}
                            className="text-sm font-bold text-fox-fire tabular-nums mr-1 cursor-pointer hover:underline underline-offset-2 focus:underline"
                            title="Vorschau öffnen"
                          >
                            {selectedDay}. {monthNames[selectedMonth]}, {post.time}
                          </button>
                        ) : (
                          <span className="text-sm font-bold text-fox-fire tabular-nums mr-1">{post.time}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${srcBadge.cls}`}>{srcBadge.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${fmtBadge.cls}`}>{fmtBadge.label}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 capitalize">{post.platform}</span>
                        {/* Unified Status-Badge — mit Fallback, damit unbekannte Status nie abstürzen */}
                        {post.status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusMeta(post.status).cls}`}>
                            {statusMeta(post.status).label}
                          </span>
                        )}
                        {post.status === 'failed' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 cursor-help" title={post.error_message}>❌ {t("Fehler", "Error")}</span>
                        )}
                        {/* Fachfreigabe-Tor (Fund 21.08.2026): 50 Beiträge des zahlenden
                            Kunden hingen daran und sahen im Plan aus wie normale Entwürfe.
                            Das Feld kam längst mit (Projektion {"_id": 0}) — gelesen hat es
                            niemand. */}
                        {fachfreigabe.markiert && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700"
                            title={fachfreigabe.hinweis}
                            data-testid={`fachfreigabe-badge-${post.id}`}
                          >
                            🔒 Fachliche Freigabe nötig
                          </span>
                        )}
                        {post.is_evergreen && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">♻️ Evergreen</span>}
                        {post.evergreen_origin_post_id && <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">♻️ Wiederholung</span>}
                        {post.brand_optimized && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">✨ Brand</span>}
                        {post.from_trend && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">⚡ Trend</span>}
                        {/* Video-Status Badge — NUR Betreiber (isMaster).
                            Video ist eine Inhaber-Funktion (kostenpflichtige Renders);
                            Kalender-Punkt (oben) und Aktions-Button (unten) sind bereits
                            isMaster-gegatet — diese Listenansicht war die letzte Lücke. */}
                        {isMaster && (post.video_status || post.video_url) && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (post.video_status === 'ready' || post.video_status === 'completed' || post.video_url)
                              ? 'bg-green-100 text-green-700'
                              : (post.video_status === 'generating' || post.video_status === 'queued')
                                ? 'bg-blue-100 text-blue-600'
                                : post.video_status === 'failed'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-stone-100 text-stone-400'
                          }`}>
                            {(post.video_status === 'ready' || post.video_status === 'completed' || (post.video_url && !post.video_status))
                              ? '🎬 Video bereit'
                              : (post.video_status === 'generating' || post.video_status === 'queued')
                                ? '⟳ Video läuft'
                                : post.video_status === 'failed'
                                  ? '✗ Video fehler'
                                  : '· Video ausstehend'}
                          </span>
                        )}
                      </div>

                      {/* ── Hook-Text ── */}
                      <p className={`text-sm text-stone-700 mb-3 leading-snug ${isDone ? 'line-through text-stone-400' : ''}`}>
                        {post.hook_full || post.hook}
                      </p>

                      {/* ── Termin überschritten (System-Status) — ruhiger Hinweis, kein Alarm ──
                          Der Scheduler hat diesen Post NICHT automatisch gepostet, weil sein Termin
                          in der Vergangenheit lag. Handlung: neu einplanen. */}
                      {/* ── Wartet auf fachliche Freigabe — ruhiger Hinweis, kein Alarm ──
                          Warum das hier steht und nicht nur im Serverlog: Der Nachlauf schrieb
                          jede Nacht „48 freigegebene Beiträge, aber KEINER eingeplant" — an eine
                          Stelle, an die kein Kunde je sieht. Im Plan sahen die Beiträge aus wie
                          normale Entwürfe. Wer nicht weiß, dass etwas hängt, fragt auch nicht.
                          Der Grundtext stammt aus dem eigenen Erkenner (feste Wortliste), nicht
                          aus Kunden- oder KI-Eingabe, und wird als Text gerendert. */}
                      {fachfreigabe.markiert && (
                        <div
                          className="mb-3 text-xs bg-purple-50 border border-purple-200 text-purple-900 rounded-lg p-3"
                          data-testid={`fachfreigabe-hinweis-${post.id}`}
                        >
                          <p className="font-semibold mb-1">Wartet auf fachliche Freigabe</p>
                          <p className="leading-relaxed">
                            {fachfreigabe.hinweis}
                          </p>
                          {fachfreigabe.grund && (
                            <p className="mt-1.5 text-purple-700">
                              Erkannt: {fachfreigabe.grund}
                            </p>
                          )}
                          <p className="mt-1.5 text-purple-600">
                            Melde dich bei uns, sobald du den Text geprüft hast — wir tragen die
                            Freigabe unter deinem Namen ein, danach wird der Beitrag ganz normal
                            eingeplant.
                          </p>
                        </div>
                      )}

                      {post.status === 'needs_reschedule' && (
                        <div className="mb-3 text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3">
                          <div className="font-medium">Termin überschritten – bitte neu einplanen.</div>
                          {formatMissedSchedule(post.scheduled_date, post.scheduled_time) && (
                            <div className="mt-0.5 text-orange-600">
                              geplant war: {formatMissedSchedule(post.scheduled_date, post.scheduled_time)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Kanal nicht verbunden (Ausnahme nach Auto-Scheduling) — ruhiger Hinweis ──
                          Der Beitrag wurde eingeplant, sein Ziel-Kanal ist aber (noch) nicht verbunden. */}
                      {post.status === 'blocked_channel' && (
                        <div className="mb-3 text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3">
                          <div className="font-medium">Kanal nicht verbunden – Beitrag wartet.</div>
                          <div className="mt-0.5 text-orange-600">Verbinde den Kanal oder stelle den Beitrag um.</div>
                        </div>
                      )}

                      {/* ── Tarif pausiert (Ausnahme) — ehrlicher Hinweis, kein Alarm ── */}
                      {post.status === 'paused_plan' && (
                        <div className="mb-3 text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3">
                          <div className="font-medium">Tarif pausiert.</div>
                          <div className="mt-0.5 text-orange-600">Kein automatisches Posten, solange der Tarif pausiert ist.</div>
                        </div>
                      )}

                      {/* ── Review-Flow: Status-Schnellwahl ── */}
                      {post.status !== 'published' && (
                        <div className="mb-3">
                          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide mb-1.5">Status</p>
                          <div className="flex items-center gap-1 flex-wrap">
                            {REVIEW_FLOW.map(s => {
                              const isActive = post.status === s.key;
                              return (
                                <button
                                  key={s.key}
                                  onClick={() => handleStatusChange(post, s.key)}
                                  disabled={isActive}
                                  className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors border ${
                                    isActive
                                      ? `${STATUS_CONFIG[s.key]?.cls} border-current ring-1 ring-current`
                                      : 'bg-white text-stone-500 border-stone-200 hover:bg-stone-50 hover:text-stone-700'
                                  }`}
                                >
                                  {s.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ── Aktionen ── */}
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        {/* Text kopieren — Übergangs-Hebel: fertigen Beitrag (Hook+Body+
                            Hashtags) in einem Klick greifen und manuell im eigenen Kanal
                            einfügen. Schreiben ist erledigt; Einfügen dauert 15 Sekunden. */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const hookT = (post.hook_full || post.hook || "").trim();
                            const bodyT = (post.body || post.core_content || post.text_content || post.content || "").trim();
                            const tags = (post.hashtags || []).map(tag => `#${String(tag).replace(/^#/, "")}`).join(" ");
                            const text = [hookT, bodyT, tags].filter(Boolean).join("\n\n");
                            const done = () => toast.success("Beitrag kopiert — in deinem Kanal einfügen");
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard.writeText(text).then(done).catch(() => toast.error("Kopieren nicht möglich"));
                            } else {
                              const el = document.createElement("textarea");
                              el.value = text; el.style.position = "fixed"; el.style.opacity = "0";
                              document.body.appendChild(el); el.select();
                              try { document.execCommand("copy"); done(); } catch { toast.error("Kopieren nicht möglich"); }
                              document.body.removeChild(el);
                            }
                            trackEvent("post_copied", { platform: post.platform || "" });
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                          title={t("Fertigen Beitrag kopieren und in deinem Kanal einfügen", "Copy the finished post and paste it into your channel")}
                          data-testid={`copy-post-${post.id}`}
                        >
                          <Copy size={11} /> {t("Text kopieren", "Copy text")}
                        </button>

                        {/* Bearbeiten */}
                        <button
                          onClick={() => {
                            setEditingPostId(post.id);
                            setEditFields({
                              hook: post.hook_full || post.hook || "",
                              time: post.time || "12:00",
                              platform: post.platform || "instagram",
                              content_type: post.content_type || "post",
                              pillar: post.pillar || "",
                            });
                            setShowNewPostForm(false);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors"
                        >
                          <Edit2 size={11} /> {t("Bearbeiten", "Edit")}
                        </button>

                        {/* Löschen (CONTENT-02) — bei bereits veröffentlichten
                            Beiträgen gar nicht erst anbieten; der Server lehnt sie
                            ohnehin mit 409 ab. */}
                        {!["published", "done"].includes(post.status) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePost(post); }}
                            disabled={deletingPostId === post.id}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-stone-100 text-stone-500 rounded-lg hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-colors"
                            title={t("Beitrag endgültig aus dem Monatsplan entfernen", "Permanently remove the post from the monthly plan")}
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 size={11} /> {deletingPostId === post.id ? t("Wird gelöscht…", "Deleting…") : t("Löschen", "Delete")}
                          </button>
                        )}

                        {/* Bilder neu erzeugen — zeichnet dieselben Inhalte in einem anderen
                            Karten-Layout neu (kein neuer Text, gleiche Reihenfolge). Nur
                            sinnvoll, wenn schon Karten/ein Bild da sind. Keine Blackbox:
                            der Tooltip sagt genau, was passiert. */}
                        {(post.card_urls?.length || post.image_url) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRegenerateCards(post); }}
                            disabled={regeneratingCardsFor === post.id}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-stone-100 text-stone-500 rounded-lg hover:bg-stone-200 disabled:opacity-50 transition-colors"
                            title={t("Zeichnet die Bilder in einem anderen Layout neu — gleiche Texte, gleiche Reihenfolge. Ändert nicht den Beitragstext.", "Redraws the images in a different layout — same text, same order. Does not change the post text.")}
                            data-testid={`regenerate-cards-${post.id}`}
                          >
                            {regeneratingCardsFor === post.id
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Image size={11} />}
                            {t("Bilder neu", "New images")}
                          </button>
                        )}

                        {/* Evergreen toggle */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEvergreenQuickToggle(post); }}
                          className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                            post.is_evergreen ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                          }`}
                          title={post.is_evergreen ? t('Evergreen deaktivieren', 'Turn off evergreen') : t('Als Evergreen markieren', 'Mark as evergreen')}
                          data-testid={`evergreen-toggle-${post.id}`}
                        >
                          <RefreshCw size={11} className={post.is_evergreen ? 'text-green-600' : ''} />
                        </button>

                        {/* Video-Aktion — via getVideoActionConfig(); nur Inhaber */}
                        {isMaster && (() => {
                          const cfg = getVideoActionConfig(post);
                          if (cfg.kind === 'link') {
                            return (
                              <a href={cfg.href} target="_blank" rel="noopener noreferrer"
                                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${cfg.className}`}
                                title={cfg.title}
                              >
                                {cfg.label}
                              </a>
                            );
                          }
                          if (cfg.kind === 'link+recreate') {
                            return (
                              <div className="flex items-center gap-1">
                                <a href={cfg.href} target="_blank" rel="noopener noreferrer"
                                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${cfg.className}`}
                                  title={cfg.title}
                                >
                                  {cfg.label}
                                </a>
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate('/video', { state: cfg.navState }); }}
                                  className="flex items-center gap-1 px-1.5 py-1 text-xs bg-stone-100 text-stone-500 rounded-lg hover:bg-stone-200 transition-colors"
                                  title="Neues Video erstellen"
                                >
                                  <Video size={11} />
                                </button>
                              </div>
                            );
                          }
                          if (cfg.kind === 'loading') {
                            return (
                              <span className="flex items-center gap-1 text-xs text-blue-600 px-2 py-1">
                                <Loader2 size={11} className="animate-spin" /> {cfg.label}
                              </span>
                            );
                          }
                          if (cfg.kind === 'info') {
                            return (
                              <span className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg ${cfg.className}`} title={cfg.title}>
                                {cfg.label}
                              </span>
                            );
                          }
                          // kind === 'retry' | 'start'
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate('/video', { state: cfg.navState }); }}
                              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${cfg.className}`}
                              title={cfg.title}
                            >
                              <Video size={11} /> {cfg.label}
                            </button>
                          );
                        })()}

                        {/* Preview */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setPreviewPost(post); setPreviewPlatform('facebook'); }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                          title={t("Facebook Vorschau", "Facebook preview")}
                        >
                          <Facebook size={11} /> <Eye size={11} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setPreviewPost(post); setPreviewPlatform('linkedin'); }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                          title={t("LinkedIn Vorschau", "LinkedIn preview")}
                        >
                          <Linkedin size={11} /> <Eye size={11} />
                        </button>

                        {/* Content Export */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setOutputPost(post); }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                          title={t("Blog & WhatsApp Export", "Blog & WhatsApp export")}
                        >
                          <FileDown size={11} /> {t("Export", "Export")}
                        </button>

                        {/* Einplanen — freigegebene Posts sowie abgelaufene Termine (needs_reschedule).
                            Öffnet den Datum/Zeit-Dialog; der Übergang nach 'scheduled' passiert ausschließlich
                            hier (mit Zukunftszeit). So schließt sich die Kette: abgelaufen -> neu einplanen -> scheduled. */}
                        {(post.status === 'approved' || post.status === 'needs_reschedule') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenScheduleDialog(post); }}
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                              post.status === 'needs_reschedule'
                                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                : 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                            }`}
                            title={post.status === 'needs_reschedule'
                              ? t('Verpassten Termin neu einplanen (Datum & Uhrzeit in der Zukunft)', 'Reschedule the missed date (date & time in the future)')
                              : t('Beitrag mit Datum & Uhrzeit einplanen', 'Schedule the post with a date & time')}
                            data-testid={`schedule-post-${post.id}`}
                          >
                            <Clock size={11} /> {post.status === 'needs_reschedule' ? t('Neu einplanen', 'Reschedule') : t('Einplanen', 'Schedule')}
                          </button>
                        )}

                        {/* Kanäle verbinden — Ausnahme 'blocked_channel'. Eine klare Aktion:
                            zum Verbinden-Bereich. Der Beitrag bleibt eingeplant und geht live,
                            sobald der Kanal verbunden ist. */}
                        {post.status === 'blocked_channel' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate('/kanale'); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                            title={t("Kanal verbinden, damit dieser Beitrag automatisch veröffentlicht wird", "Connect the channel so this post can publish automatically")}
                            data-testid={`connect-channel-${post.id}`}
                          >
                            <Wifi size={11} /> {t("Kanäle verbinden", "Connect channels")}
                          </button>
                        )}

                        {/* Aus Planung nehmen — nur geplante Posts. Sicherer Rückweg aus der Auto-Post-Kette (-> Entwurf). */}
                        {post.status === 'scheduled' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnschedule(post); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors"
                            title={t("Beitrag aus der Planung nehmen (zurück auf Entwurf)", "Remove the post from the schedule (back to draft)")}
                            data-testid={`unschedule-post-${post.id}`}
                          >
                            <X size={11} /> {t("Aus Planung nehmen", "Remove from schedule")}
                          </button>
                        )}

                        {/* Live Posting — nur für freigabe-bereite Posts, mit Vorschau-Dialog.
                            ONB-02 (16.08.2026): Der Knopf erschien unabhängig davon, ob der
                            Zielkanal überhaupt verbunden ist. Der Onboarding-Assistent schlägt
                            standardmäßig Bluesky + Instagram + Facebook vor, postbereit ist in
                            der Testphase aber nur Bluesky — ein Erstkunde lief also mit hoher
                            Wahrscheinlichkeit in einen Fehlschlag, den er selbst ausgelöst hat.
                            Bei nicht verbundenem Kanal führt jetzt derselbe Weg wie bei
                            'blocked_channel': zum Verbinden, nicht in den Fehler. */}
                        {["approved", "ready", "scheduled"].includes(post.status) && (
                          istVerbunden(post.platform) ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPreviewMode("publish"); setPublishPreviewPost(post); }}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                              title={t(`Vorschau & Veröffentlichen auf ${post.platform}`, `Preview & publish on ${post.platform}`)}
                            >
                              <Send size={11} />
                              {t("Jetzt posten", "Post now")}
                            </button>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate('/kanale'); }}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                              title={t(`${post.platform} ist noch nicht verbunden — hier verbinden, dann kannst du veröffentlichen`, `${post.platform} isn't connected yet — connect it here, then you can publish`)}
                              data-testid={`connect-before-publish-${post.id}`}
                            >
                              <Wifi size={11} />
                              {t("Kanal verbinden", "Connect channel")}
                            </button>
                          )
                        )}
                        {/* Retry-Button für fehlgeschlagene Posts */}
                        {post.status === 'failed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setPreviewMode("publish"); setPublishPreviewPost(post); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                            title={t("Erneut versuchen", "Try again")}
                          >
                            <Send size={11} />
                            {t("Erneut posten", "Post again")}
                          </button>
                        )}
                      </div>

                      {/* ── Notizen / Freigabe-Feedback ── */}
                      <div className="pt-3 border-t border-stone-100">
                        <label className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide block mb-1.5">
                          Notizen &amp; Feedback
                        </label>
                        <textarea
                          rows={2}
                          placeholder="Freigabehinweis, Feedback, Absprachen…"
                          value={notesValues[post.id] !== undefined ? notesValues[post.id] : (post.notes || "")}
                          onChange={(e) => setNotesValues(prev => ({ ...prev, [post.id]: e.target.value }))}
                          onBlur={(e) => handleSaveNotes(post.id, e.target.value)}
                          className="w-full text-xs border border-stone-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-stone-300 resize-none bg-stone-50 placeholder-stone-300"
                        />
                        <div className="flex items-center justify-between mt-1">
                          {post.notes_updated_at ? (
                            <p className="text-[10px] text-stone-300">
                              Gespeichert: {formatNotesDate(post.notes_updated_at)}
                            </p>
                          ) : (
                            <span />
                          )}
                          {savingNotes === post.id && (
                            <span className="text-[10px] text-stone-400 flex items-center gap-1">
                              <Loader2 size={9} className="animate-spin" /> Speichert…
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ── Qualitäts-Hinweise (interne Ansicht, nur wenn Audit gelaufen) ── */}
                      {showQaUi && auditMap[post.id] && auditMap[post.id].quality !== "good" && (
                        <div className={`pt-3 border-t ${
                          auditMap[post.id].quality === "weak"
                            ? "border-red-100"
                            : "border-amber-100"
                        }`}>
                          <div className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${
                            auditMap[post.id].quality === "weak"
                              ? "bg-red-50 border border-red-100"
                              : "bg-amber-50 border border-amber-100"
                          }`}>
                            <AlertTriangle size={12} className={`flex-shrink-0 mt-0.5 ${
                              auditMap[post.id].quality === "weak" ? "text-red-500" : "text-amber-500"
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold mb-1 ${
                                auditMap[post.id].quality === "weak" ? "text-red-700" : "text-amber-700"
                              }`}>
                                {auditMap[post.id].short_label}
                              </p>
                              <ul className="space-y-0.5">
                                {auditMap[post.id].reasons.map((r, ri) => (
                                  <li key={ri} className="text-stone-500 flex items-center gap-1">
                                    <span className="text-[10px]">·</span> {r}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── Content-Varianten ── */}
                      {(() => {
                        const pid   = post.id;
                        const hasContent = !!(post.hook_full || post.hook || post.core_content || post.text_content);
                        const varState  = variantsMap[pid];
                        const isLoading = varState?.loading;
                        const varItems  = varState?.items || [];
                        const varError  = varState?.error;
                        const isOpen    = !!varState;

                        return (
                          <div className="pt-3 border-t border-stone-100">
                            {/* Trigger-Zeile */}
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide">
                                Alternative Formulierungen
                              </p>
                              <div className="flex items-center gap-1.5">
                                {isOpen && (
                                  <button
                                    onClick={() => setVariantsMap(prev => { const n = {...prev}; delete n[pid]; return n; })}
                                    className="text-[10px] text-stone-400 hover:text-stone-600 transition-colors"
                                  >
                                    Schließen
                                  </button>
                                )}
                                <button
                                  onClick={() => handleGenerateVariants(post)}
                                  disabled={isLoading || !hasContent}
                                  className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                                    !hasContent
                                      ? 'bg-stone-50 text-stone-300 border-stone-100 cursor-not-allowed'
                                      : isLoading
                                        ? 'bg-violet-50 text-violet-400 border-violet-100 cursor-wait'
                                        : 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
                                  }`}
                                  title={!hasContent ? "Noch kein Inhalt vorhanden" : "3 Textvarianten generieren"}
                                >
                                  {isLoading
                                    ? <><Loader2 size={10} className="animate-spin" /> Generiere…</>
                                    : <><Sparkles size={10} /> Varianten erzeugen</>
                                  }
                                </button>
                              </div>
                            </div>

                            {/* Fehlermeldung */}
                            {varError && (
                              <p className="text-[10px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5 mb-2">
                                {varError}
                              </p>
                            )}

                            {/* Varianten-Karten */}
                            {varItems.length > 0 && (
                              <div className="space-y-2 mt-1">
                                {varItems.map((variant, vi) => (
                                  <div
                                    key={vi}
                                    className="bg-violet-50/60 border border-violet-100 rounded-xl p-3 space-y-1.5"
                                  >
                                    {/* Varianten-Header */}
                                    <div className="flex items-center justify-between gap-2">
                                      <div>
                                        <span className="text-xs font-semibold text-violet-800">
                                          {variant.title}
                                        </span>
                                        {variant.focus && (
                                          <span className="text-[10px] text-violet-500 ml-2">
                                            — {variant.focus}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => handleAdoptVariant(post, variant)}
                                        disabled={adoptingVariant === pid}
                                        className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
                                        title="Diesen Text als neuen Post-Inhalt übernehmen"
                                      >
                                        {adoptingVariant === pid
                                          ? <Loader2 size={10} className="animate-spin" />
                                          : <CheckCircle size={10} />
                                        }
                                        Übernehmen
                                      </button>
                                    </div>

                                    {/* Hook */}
                                    {variant.hook && (
                                      <p className="text-xs font-semibold text-stone-800 leading-snug">
                                        {variant.hook}
                                      </p>
                                    )}

                                    {/* Text-Preview */}
                                    {variant.text && (
                                      <p className="text-[11px] text-stone-600 leading-relaxed">
                                        {variant.text.length > 200
                                          ? variant.text.slice(0, 200) + "…"
                                          : variant.text
                                        }
                                      </p>
                                    )}
                                  </div>
                                ))}

                                {/* Hinweis */}
                                <p className="text-[10px] text-stone-400 pt-1">
                                  Klick auf „Übernehmen" ersetzt Hook und Text des Posts unwiderruflich.
                                </p>
                              </div>
                            )}

                            {/* Leer-Zustand wenn noch nicht generiert */}
                            {!isLoading && !varError && varItems.length === 0 && !isOpen && hasContent && (
                              <p className="text-[10px] text-stone-300 mt-0.5">
                                Klick auf „Varianten erzeugen" für 3 alternative Formulierungen.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              );
            })}

            {getPostsForDay(selectedDay).length === 0 && !showNewPostForm && (
              <div className="py-8 text-center text-stone-400">
                <Calendar size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Noch keine Posts für diesen Tag.</p>
                <button
                  onClick={() => { resetNewPostForm(); setShowNewPostForm(true); }}
                  className="mt-3 text-xs text-fox-fire hover:underline"
                >
                  + Manuellen Post erstellen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch-Refresh-Panel */}
      {batchOpen && (
        <BatchRefreshPanel
          month={selectedMonth}
          year={selectedYear}
          onClose={() => setBatchOpen(false)}
          onDone={({ adopted, skipped }) => {
            setBatchOpen(false);
            if (adopted > 0) {
              toast.success(`✅ ${adopted} Beitrag${adopted > 1 ? 'e' : ''} verbessert!`);
              fetchCalendar();
              handleRunAudit(); // Audit aktualisieren
            } else {
              toast.info("Keine Beiträge übernommen.");
            }
          }}
        />
      )}

      {/* ── Performance-Insights (ausgeblendet wenn keine Daten) ─────────── */}
      <AnalyticsInsights platforms={["instagram", "facebook", "linkedin"]} />

      {/* ── Empfehlungen (ausgeblendet wenn keine vorhanden) ─────────────── */}
      <AutopilotRecommendations
        platforms={["instagram", "facebook", "linkedin"]}
        month={selectedMonth}
        year={selectedYear}
        onOpenAutopilot={scrollToWizard}
      />

      {/* ── Autopilot-Wizard ───────────────────────────────────────────────── */}
      <div ref={wizardRef} className="bg-white rounded-xl border border-stone-200 p-6">
        <AutopilotWizard onSuccess={fetchCalendar} />
      </div>

      {/* ── Plan-Refresh & Vergleich ────────────────────────────────────────── */}
      <PlanRefreshComparison
        month={selectedMonth}
        year={selectedYear}
        platforms={["instagram", "facebook", "linkedin"]}
        onSuccess={fetchCalendar}
      />

      {/* ── Autopilot-Historie (ausgeblendet wenn keine Daten) ──────────────── */}
      <AutopilotHistory limit={8} />

      {/* ── Erweiterter Generator (Admin / Legacy) ───────────────────────────
          CONTENT-01b (16.08.2026): Der Abschnitt hieß schon immer „Admin / Legacy",
          war aber für JEDEN Kunden sichtbar und klickbar — nur der Video-Umschalter
          darin trug einen isMaster-Riegel. Ein Klick schrieb 100 fest verdrahtete
          Förderfuchs-Werbebeiträge in den Kalender des Kunden. Der Riegel gehört um
          den ganzen Block, nicht um ein Kästchen darin. Das Backend lehnt den Aufruf
          seit derselben Reparatur zusätzlich mit 403 ab (require_master) — zwei
          Riegel, weil ein Frontend-Riegel allein per DevTools zu umgehen ist. */}
      {isMaster && (
      <details className="group">
        <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-stone-500 hover:text-stone-700 transition-colors list-none select-none">
          <ChevronRight size={14} className="group-open:rotate-90 transition-transform" />
          Erweiterte Optionen (Legacy-Generator)
        </summary>
        <div className="mt-4 bg-gradient-to-br from-fox-fire/10 via-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Monat</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full p-3 border border-stone-200 rounded-lg bg-white"
            >
              {monthNames.slice(1).map((name, i) => (
                <option key={i + 1} value={i + 1}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Jahr</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full p-3 border border-stone-200 rounded-lg bg-white"
            >
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
          {/* Video-Umschalter nur für den Inhaber — Kunden bekommen keine Videos. */}
          {isMaster && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Videos</label>
            <button
              onClick={() => setIncludeVideos(!includeVideos)}
              className={`w-full p-3 rounded-lg border flex items-center justify-center gap-2 ${
                includeVideos ? 'bg-fox-fire text-white border-fox-fire' : 'bg-white border-stone-200'
              }`}
            >
              <Video size={18} />
              {includeVideos ? 'Mit Videos' : 'Ohne Videos'}
            </button>
          </div>
          )}
          <div className="flex items-end">
            <button
              data-testid="generate-monthly-btn"
              onClick={handleGenerateMonthlyPlan}
              disabled={generating}
              className="w-full btn-primary flex items-center justify-center gap-2 h-12"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generiere 100 Posts...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  100 Posts generieren
                </>
              )}
            </button>
          </div>
        </div>

        {/* Theme Distribution Info */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { name: 'Gründung', count: 15 },
            { name: 'Gründungscoaching', count: 15 },
            { name: 'Bewerbung', count: 12 },
            { name: 'Bewerbungscoaching', count: 12 },
            { name: 'Bewerbungsfoto App', count: 16 },
            { name: 'Motivation', count: 30 }
          ].map(theme => (
            <div key={theme.name} className={`p-3 rounded-lg border ${themeColors[theme.name]}`}>
              <div className="text-lg font-bold">{theme.count}</div>
              <div className="text-xs truncate">{theme.name}</div>
            </div>
          ))}
        </div>
        </div>
      </details>
      )}

      {/* Perplexity Trend-Recherche — ausgeblendet solange kein PERPLEXITY_API_KEY:
          ohne Key liefert das Backend nur hartkodierte Demo-Ideen (s. config/features.js) */}
      {TREND_RESEARCH_ENABLED && (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Lightbulb size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-amber-900">Perplexity Pro Trend-Recherche</h3>
              <p className="text-sm text-amber-600">
                KI-gestützte Content-Ideen für AVGS, Gründung & Bewerbung
              </p>
            </div>
          </div>
          <button
            data-testid="open-trend-modal-btn"
            onClick={() => setShowTrendModal(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
          >
            <Search size={18} />
            Trends mit Perplexity holen
          </button>
        </div>
      </div>
      )}

      {/* Stats after generation */}
      {stats && (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-center gap-2 text-green-600 mb-4">
            <CheckCircle size={24} />
            <span className="font-heading text-xl font-bold">{stats.total_posts} Posts generiert!</span>
          </div>
          <p className="text-stone-600">{stats.message}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {stats.platforms?.map(p => (
              <span key={p} className={`platform-badge ${platformColors[p] || 'bg-stone-100 text-stone-600'}`}>
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Smart Scheduling Status */}
      {smartScheduleStatus && (
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <Clock size={20} className="text-teal-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-teal-900">KI-Scheduler</h3>
                <p className="text-sm text-teal-600">
                  {smartScheduleStatus.system_message}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-teal-700">{smartScheduleStatus.learning_platforms}</div>
              <div className="text-xs text-teal-500">Lernende Plattformen</div>
            </div>
          </div>
          
          {/* Platform Status Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(smartScheduleStatus.platforms || {}).map(([platform, status]) => (
              <div 
                key={platform}
                className={`p-3 rounded-lg border ${
                  status.is_learning 
                    ? 'bg-teal-100 border-teal-300' 
                    : 'bg-white border-stone-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${
                    status.is_learning ? 'bg-teal-500' : 'bg-stone-300'
                  }`} />
                  <span className="font-medium text-sm capitalize">{platform}</span>
                </div>
                <div className="text-xs text-stone-500">
                  {status.is_learning ? (
                    <span className="text-teal-600">Auto (Lernend)</span>
                  ) : (
                    <span>Auto (Regel)</span>
                  )}
                </div>
                <div className="text-xs text-stone-400 mt-1">
                  {status.total_posts_analyzed} Posts analysiert
                </div>
              </div>
            ))}
          </div>
          
          {/* Info Text */}
          <div className="mt-4 p-3 bg-teal-100/50 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-teal-700">
                <strong>So funktioniert's:</strong> Der KI-Scheduler lernt aus der Performance deiner Posts 
                und passt Posting-Zeiten automatisch an. Nach etwa 20 Posts pro Plattform beginnt das System zu lernen.
              </p>
              {lastScheduleUpdate && (
                <div className="text-xs text-teal-600 ml-4 whitespace-nowrap">
                  <Clock size={12} className="inline mr-1" />
                  Letzte Aktualisierung: {lastScheduleUpdate.relative_time}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Generation Status — reines Inhaber-Werkzeug */}
      {isMaster && videoStatus && (
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Video size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-purple-900">Kurzvideo-Generierung</h3>
                <p className="text-sm text-purple-600">
                  {videoStatus.mode === 'simulation' ? '⚪ Vorschau-Modus' : '🟣 Live-Modus'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-700">{videoStatus.usage?.remaining || 0}</div>
              <div className="text-xs text-purple-500">Videos heute verfügbar</div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 bg-purple-100 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-purple-500 transition-all" 
                style={{ width: `${((videoStatus.usage?.videos_generated || 0) / 20) * 100}%` }}
              />
            </div>
            <span className="text-sm text-purple-700">
              {videoStatus.usage?.videos_generated || 0} / {videoStatus.usage?.limit || 20} heute
            </span>
          </div>
          
          <div className="flex flex-wrap gap-3 mt-4">
            {/* 20er-Batch ausgeblendet (22.07.): synchron-sequenziell in EINEM Request —
                20 Renders ≈ >60 Min, Browser/Proxy kappen nach Minuten. Einzel-Video
                (VideoPage) funktioniert; wieder aktivieren erst nach Async-Umbau
                (video_routes-TODO) via REACT_APP_VIDEO_BATCH_ENABLED=true + Rebuild. */}
            {VIDEO_BATCH_ENABLED && (
            <button
              data-testid="batch-video-btn"
              onClick={handleGenerateAllVideos}
              disabled={batchGenerating || (videoStatus.usage?.remaining || 0) <= 0}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {batchGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generiere Videos...
                </>
              ) : (
                <>
                  <Video size={16} />
                  Für alle Posts Videos generieren
                </>
              )}
            </button>
            )}
            <button
              onClick={() => setShowVideoLogs(!showVideoLogs)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <FileText size={16} />
              {showVideoLogs ? 'Logs ausblenden' : 'Video-Logs anzeigen'}
            </button>
            <button
              onClick={fetchVideoStatus}
              className="flex items-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-700"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          
          {/* Warnung — gehört zum Batch-Button, wandert mit ihm hinter das Flag */}
          {VIDEO_BATCH_ENABLED && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <strong>⚠️ Hinweis:</strong> Die Batch-Generierung kann Credits/Compute kosten!
            Maximal 20 Videos pro Tag, um Kosten zu kontrollieren.
          </div>
          )}
          
          {/* Video Logs */}
          {showVideoLogs && videoLogs.length > 0 && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-purple-100 max-h-64 overflow-y-auto">
              <h4 className="font-medium text-purple-900 mb-2">Letzte Video-Generierungen</h4>
              <div className="space-y-2">
                {videoLogs.map((log, index) => (
                  <div 
                    key={index}
                    className={`p-2 rounded text-sm flex items-center justify-between ${
                      log.status === 'success' || log.status === 'simulated' ? 'bg-green-50' :
                      log.status === 'rate_limited' ? 'bg-amber-50' : 'bg-red-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        log.status === 'success' || log.status === 'simulated' ? 'bg-green-200 text-green-800' :
                        log.status === 'rate_limited' ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'
                      }`}>
                        {log.status === 'simulated' ? 'Vorschau' : log.status}
                      </span>
                      <span className="text-stone-600">{log.channel}</span>
                      {log.prompt_preview && (
                        <span className="text-stone-500 text-xs truncate max-w-[200px]">"{log.prompt_preview}..."</span>
                      )}
                    </div>
                    <span className="text-xs text-stone-400">
                      {log.timestamp ? new Date(log.timestamp).toLocaleTimeString('de-DE') : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Social Media Auto-Posting (ECHT — veröffentlicht live).
          Anker-Ziel des Header-Loops „Veröffentlichen" (scroll-mt für fixen Header). */}
      <div id="veroeffentlichen" className="scroll-mt-32 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Share2 size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-blue-900">Social Media Auto-Posting</h3>
              <p className="text-sm text-blue-600">
                {t("Veröffentlicht ausgewählte Beiträge sofort live auf den verbundenen Kanälen.", "Publishes selected posts immediately, live, on your connected channels.")}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowSocialLogs(!showSocialLogs)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50"
          >
            <FileText size={16} />
            {showSocialLogs ? t('Logs ausblenden', 'Hide logs') : t('Logs anzeigen', 'Show logs')}
          </button>
        </div>
        
        {/* Platform Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Facebook Card */}
          <div className="bg-white rounded-lg border border-blue-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Facebook size={16} className="text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-stone-900">Facebook</h4>
                  <p className="text-xs text-stone-500">
                    {istVerbunden('facebook') ? t('🟢 Verbunden', '🟢 Connected') : t('⚪ Kanal nicht verbunden', '⚪ Channel not connected')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-700">
                  {/* Kein erfundener Vorgabewert. Hier stand `?? 5`: Fehlte der Zähler,
                      zeigte die Oberfläche trotzdem eine plausible Zahl — „5 Posts
                      verfügbar" bei null Datenlage. Unbekannt muss unbekannt aussehen. */}
                  {socialStatus?.facebook?.usage?.remaining ?? '–'}
                </div>
                <div className="text-xs text-stone-500">{t("Posts verfügbar", "Posts available")}</div>
              </div>
            </div>
            <button
              data-testid="facebook-batch-post-btn"
              onClick={() => handleBatchSocialPost('facebook')}
              disabled={postingPlatform === 'facebook'}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {postingPlatform === 'facebook' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t("Veröffentliche...", "Publishing...")}</>
              ) : (
                <><Send size={16} /> {t("Posts veröffentlichen", "Publish posts")}</>
              )}
            </button>
          </div>

          {/* LinkedIn Card */}
          <div className="bg-white rounded-lg border border-blue-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-700 rounded-lg flex items-center justify-center">
                  <Linkedin size={16} className="text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-stone-900">LinkedIn</h4>
                  <p className="text-xs text-stone-500">
                    {istVerbunden('linkedin') ? t('🟢 Verbunden', '🟢 Connected') : t('⚪ Kanal nicht verbunden', '⚪ Channel not connected')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-700">
                  {/* wie oben: vorher `?? 3` */}
                  {socialStatus?.linkedin?.usage?.remaining ?? '–'}
                </div>
                <div className="text-xs text-stone-500">{t("Posts verfügbar", "Posts available")}</div>
              </div>
            </div>
            <button
              data-testid="linkedin-batch-post-btn"
              onClick={() => handleBatchSocialPost('linkedin')}
              disabled={postingPlatform === 'linkedin'}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
            >
              {postingPlatform === 'linkedin' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t("Veröffentliche...", "Publishing...")}</>
              ) : (
                <><Send size={16} /> {t("Posts veröffentlichen", "Publish posts")}</>
              )}
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <strong>⚠️ {t("Wichtig:", "Important:")}</strong> {t(
            '„Posts veröffentlichen" stellt die Beiträge sofort live auf deinen verbundenen Kanälen ein.',
            '"Publish posts" puts the posts live on your connected channels immediately.',
          )}
          {" "}{t("Videos werden übersprungen, solange das Video-Setup nicht abgeschlossen ist.", "Videos are skipped until the video setup is complete.")}
        </div>

        {/* Social Logs */}
        {showSocialLogs && socialLogs.length > 0 && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100 max-h-64 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-blue-900">{t("Letzte Social Media Aktionen", "Recent social media activity")}</h4>
              <button
                onClick={fetchSocialStatus}
                className="text-blue-600 hover:text-blue-700"
              >
                <RefreshCw size={16} />
              </button>
            </div>
            <div className="space-y-2">
              {socialLogs.map((log, index) => (
                <div 
                  key={index}
                  className={`p-2 rounded text-sm flex items-center justify-between ${
                    log.status === 'success' || log.status === 'simulated' || log.status === 'scheduled' ? 'bg-green-50' :
                    log.status === 'rate_limited' ? 'bg-amber-50' : 'bg-stone-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded flex items-center justify-center ${
                      log.platform === 'facebook' ? 'bg-blue-600' : 'bg-blue-700'
                    }`}>
                      {log.platform === 'facebook' ? 
                        <Facebook size={12} className="text-white" /> : 
                        <Linkedin size={12} className="text-white" />
                      }
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      log.status === 'simulated' || log.status === 'scheduled' ? 'bg-blue-200 text-blue-800' :
                      log.status === 'rate_limited' ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'
                    }`}>
                      {log.status === 'simulated' ? t('Vorschau', 'Preview') : log.status}
                    </span>
                    <span className="text-stone-600 capitalize">{log.action}</span>
                    {log.content_preview && (
                      <span className="text-stone-500 text-xs truncate max-w-[150px]">"{log.content_preview}..."</span>
                    )}
                  </div>
                  <span className="text-xs text-stone-400">
                    {log.timestamp ? new Date(log.timestamp).toLocaleTimeString('de-DE') : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {showSocialLogs && socialLogs.length === 0 && (
          <div className="mt-4 p-4 bg-white rounded-lg border border-blue-100 text-center text-stone-500">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>{t("Noch keine Social Media Aktionen geloggt.", "No social media activity logged yet.")}</p>
            <p className="text-sm">{t("Veröffentliche Beiträge, um hier die Aktionen zu sehen.", "Publish posts to see the activity here.")}</p>
          </div>
        )}
      </div>

      {/* Publish-Preview-Dialog */}
      {publishPreviewPost && (
        <PublishPreviewDialog
          post={publishPreviewPost}
          mode={previewMode}
          onClose={() => { setPublishPreviewPost(null); setPreviewMode("publish"); }}
          onSuccess={(data) => {
            setPublishPreviewPost(null);
            setPreviewMode("publish");
            if (data.simulated) {
              toast.success(t(
                `📝 Simuliert auf ${publishPreviewPost.platform} — Plattform nicht verbunden`,
                `📝 Simulated on ${publishPreviewPost.platform} — platform not connected`,
              ));
            } else {
              toast.success(t(
                `✅ Live auf ${publishPreviewPost.platform} veröffentlicht!`,
                `✅ Published live on ${publishPreviewPost.platform}!`,
              ));
            }
            fetchCalendar();
          }}
        />
      )}

      {/* Preview Modals */}
      {previewPost && previewPlatform === 'facebook' && (
        <FacebookPreviewModal 
          post={previewPost} 
          onClose={() => { setPreviewPost(null); setPreviewPlatform(null); }} 
        />
      )}
      {previewPost && previewPlatform === 'linkedin' && (
        <LinkedInPreviewModal 
          post={previewPost} 
          onClose={() => { setPreviewPost(null); setPreviewPlatform(null); }} 
        />
      )}

      {/* Output Format Modal */}
      {outputPost && (
        <OutputFormatModal
          post={outputPost}
          onClose={() => setOutputPost(null)}
        />
      )}

      {/* ── Einplanen-Dialog (approved -> scheduled) ── */}
      {schedulingPost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={handleCloseScheduleDialog}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-stone-800 flex items-center gap-2">
                <Clock size={18} className="text-sky-600" /> {schedulingPost.status === 'needs_reschedule' ? 'Beitrag neu einplanen' : 'Beitrag einplanen'}
              </h3>
              <button
                onClick={handleCloseScheduleDialog}
                className="text-stone-400 hover:text-stone-600"
                title="Schließen"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide block mb-1">Datum</label>
                <input
                  type="date"
                  value={scheduleFields.date}
                  onChange={(e) => { setScheduleFields(prev => ({ ...prev, date: e.target.value })); setScheduleError(""); }}
                  className="w-full text-sm border border-stone-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid="schedule-date-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide block mb-1">Uhrzeit</label>
                <input
                  type="time"
                  value={scheduleFields.time}
                  onChange={(e) => { setScheduleFields(prev => ({ ...prev, time: e.target.value })); setScheduleError(""); }}
                  className="w-full text-sm border border-stone-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid="schedule-time-input"
                />
              </div>
            </div>

            {/* Kanal-Ehrlichkeit: Was geht wirklich automatisch live? */}
            {platformAutoPublishes(schedulingPost.platform) ? (
              <div className="mb-4 text-xs bg-green-50 border border-green-200 text-green-800 rounded-lg p-3">
                <strong>{schedulingPost.platform}</strong> wird zum geplanten Zeitpunkt automatisch veröffentlicht.
              </div>
            ) : (
              <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
                Dieser Kanal{schedulingPost.platform ? ` (${schedulingPost.platform})` : ""} wird noch nicht automatisch
                veröffentlicht — der Beitrag bleibt geplant, erscheint aber noch nicht von selbst.
                <div className="mt-1 text-amber-700">
                  Automatisch veröffentlicht werden derzeit <strong>LinkedIn</strong> und <strong>Bluesky</strong>.
                  Andere Kanäle sind in Vorbereitung — du kannst den Beitrag über „Jetzt posten“ oder „Text kopieren“ selbst einstellen.
                </div>
              </div>
            )}

            {scheduleError && (
              <div className="mb-4 text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg p-3" data-testid="schedule-error">
                {scheduleError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={handleCloseScheduleDialog}
                className="px-3 py-2 text-sm rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSubmitSchedule}
                disabled={schedulingSaving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60 transition-colors"
                data-testid="schedule-submit"
              >
                {schedulingSaving ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Perplexity Trend Modal — Gürtel+Hosenträger: Button ist bei ausgeschaltetem Flag weg */}
      {TREND_RESEARCH_ENABLED && showTrendModal && (
        <TrendResearchModal
          onClose={() => setShowTrendModal(false)}
          onSelectIdea={handleTrendIdeaToPost}
          onPostCreated={(post) => {
            fetchCalendar();
            toast.success(`⚡ Post "${post.hook?.substring(0, 30)}..." für ${post.channel} am ${post.scheduled_date} erstellt!`);
          }}
        />
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-heading text-lg font-bold text-blue-900 mb-2">📅 Natürliches Timing</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Motivation:</strong> Täglich 7-9 Uhr morgens (zufällige Minute)</li>
          <li>• <strong>Andere Themen:</strong> Werktags 7-22 Uhr, Wochenende 9-20 Uhr</li>
          <li>• <strong>Nie gleiche Uhrzeit:</strong> Jeder Post hat eine individuelle Zeit</li>
          <li>• <strong>6 Plattformen:</strong> Instagram, Facebook, LinkedIn, Google Business, Blog, WhatsApp</li>
        </ul>
      </div>
      
      {/* Brand Voice Info */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <h3 className="font-heading text-lg font-bold text-purple-900 mb-2">✨ Förderfuchs Brand Voice</h3>
        <p className="text-sm text-purple-800 mb-3">
          Alle Posts mit "✨ Brand optimiert" folgen der Förderfuchs-Markenstimme: 
          <strong> Professionell, motivierend, verständlich – immer per Du.</strong>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-white/50 rounded-lg p-2">
            <div className="font-medium text-purple-900">Instagram</div>
            <div className="text-purple-700">10-15 Hashtags</div>
          </div>
          <div className="bg-white/50 rounded-lg p-2">
            <div className="font-medium text-purple-900">Facebook</div>
            <div className="text-purple-700">3-5 Hashtags</div>
          </div>
          <div className="bg-white/50 rounded-lg p-2">
            <div className="font-medium text-purple-900">LinkedIn</div>
            <div className="text-purple-700">3-5 Hashtags</div>
          </div>
          <div className="bg-white/50 rounded-lg p-2">
            <div className="font-medium text-purple-900">Blog</div>
            <div className="text-purple-700">SEO-Keywords</div>
          </div>
        </div>
      </div>
    </div>

    {/* ── Kalender-Tour (nach Onboarding) ── */}
    <CalendarTour
      active={tourActive}
      hasBonusPosts={hasBonusPosts}
      onClose={() => setTourActive(false)}
    />
    </>
  );
};

// Calendar Page
const ContentCalendar = () => {
  const { language } = useLanguage();
  const dateLocale = language === "en" ? enUS : de;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [content, setContent] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, contentRes] = await Promise.all([
          axios.get(`${API}/calendar`),
          axios.get(`${API}/content`)
        ]);
        setEvents(eventsRes.data);
        setContent(contentRes.data);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
    };
    fetchData();
  }, []);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getEventsForDay = (day) => {
    return events.filter(e => {
      const eventDate = new Date(e.scheduled_at);
      return isSameDay(eventDate, day);
    });
  };

  return (
    <div data-testid="calendar-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">Content Kalender</h1>
          <p className="text-stone-500 mt-1">Plane und verwalte deine Posts</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="font-medium px-4">
            {format(weekStart, 'MMMM yyyy', { locale: dateLocale })}
          </span>
          <button 
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-stone-200">
          {weekDays.map((day) => (
            <div 
              key={day.toISOString()} 
              className={`p-4 text-center border-r last:border-r-0 ${
                isSameDay(day, new Date()) ? 'bg-fox-fire/5' : ''
              }`}
            >
              <div className="text-xs text-stone-500 uppercase">
                {format(day, 'EEE', { locale: dateLocale })}
              </div>
              <div className={`text-2xl font-bold mt-1 ${
                isSameDay(day, new Date()) ? 'text-fox-fire' : ''
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[400px]">
          {weekDays.map((day) => {
            const dayEvents = getEventsForDay(day);
            return (
              <div 
                key={day.toISOString()} 
                className="border-r last:border-r-0 p-2 space-y-2"
              >
                {dayEvents.map(event => (
                  <div 
                    key={event.id}
                    className="p-2 bg-fox-fire/10 rounded-lg text-xs"
                  >
                    <p className="font-medium text-fox-fire truncate">{event.title}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {event.platforms?.map(p => (
                        <span key={p} className={`px-1.5 py-0.5 rounded text-[10px] ${platformColors[p]}`}>
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Unscheduled Content */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Ungeplanter Content</h2>
        <div className="space-y-3">
          {content.filter(c => !c.scheduled_at).map(item => (
            <ContentRow key={item.id} item={item} />
          ))}
          {content.filter(c => !c.scheduled_at).length === 0 && (
            <p className="text-stone-500 text-center py-4">Kein ungeplanter Content</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Campaigns Page
const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    target_audience: 'Arbeitssuchende',
    platforms: ['facebook']
  });
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kampagnen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('Bitte gib einen Namen ein');
      return;
    }

    try {
      await axios.post(`${API}/campaigns`, newCampaign);
      toast.success('Kampagne erstellt!');
      setShowModal(false);
      setNewCampaign({ name: '', description: '', target_audience: 'Arbeitssuchende', platforms: ['facebook'] });
      fetchCampaigns();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Kampagne');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Kampagne wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success('Kampagne gelöscht');
      fetchCampaigns();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const allPlatforms = ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube', 'twitter', 'google_business'];

  const togglePlatform = (platform) => {
    setNewCampaign(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  return (
    <div data-testid="campaigns-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">Kampagnen</h1>
          <p className="text-stone-500 mt-1">Verwalte deine Marketing-Kampagnen</p>
        </div>
        <button
          data-testid="create-campaign-btn"
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Neue Kampagne
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <Megaphone className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-stone-900 mb-2">Keine Kampagnen</h2>
          <p className="text-stone-500 mb-4">Erstelle deine erste Kampagne</p>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            Kampagne erstellen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <div 
              key={campaign.id} 
              className="bg-white rounded-xl border border-stone-200 p-6 card-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-fox-fire/10 rounded-xl flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-fox-fire" />
                </div>
                <button
                  onClick={() => handleDeleteCampaign(campaign.id)}
                  className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <h3 className="font-heading text-lg font-bold text-stone-900">{campaign.name}</h3>
              <p className="text-sm text-stone-500 mt-1 line-clamp-2">{campaign.description || 'Keine Beschreibung'}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {campaign.platforms?.map(p => (
                  <span key={p} className={`platform-badge ${platformColors[p]}`}>
                    {p}
                  </span>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 text-xs text-stone-500">
                Zielgruppe: {campaign.target_audience}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold">Neue Kampagne</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-stone-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                <input
                  data-testid="campaign-name-input"
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Bewerbungscoaching Q1 2026"
                  className="w-full p-3 border border-stone-200 rounded-lg focus:border-fox-fire focus:ring-2 focus:ring-fox-fire/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Beschreibung</label>
                <textarea
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Beschreibe die Kampagne..."
                  className="w-full p-3 border border-stone-200 rounded-lg h-24 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Zielgruppe</label>
                <select
                  value={newCampaign.target_audience}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, target_audience: e.target.value }))}
                  className="w-full p-3 border border-stone-200 rounded-lg"
                >
                  <option value="Arbeitssuchende">Arbeitssuchende</option>
                  <option value="Gründer">Gründer</option>
                  <option value="Berufseinsteiger">Berufseinsteiger</option>
                  <option value="Alle">Alle</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Plattformen</label>
                <div className="flex flex-wrap gap-2">
                  {allPlatforms.map(p => (
                    <button
                      key={p}
                      onClick={() => togglePlatform(p)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        newCampaign.platforms.includes(p)
                          ? 'bg-fox-fire text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {p.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 btn-secondary"
              >
                Abbrechen
              </button>
              <button
                data-testid="save-campaign-btn"
                onClick={handleCreateCampaign}
                className="flex-1 btn-primary"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Media Library Page
const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${API}/media`);
        setMedia(response.data);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  return (
    <div data-testid="media-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900">Media Library</h1>
        <p className="text-stone-500 mt-1">Deine generierten Videos und Bilder</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
        </div>
      ) : media.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
          <Image className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-stone-900 mb-2">Keine Medien</h2>
          <p className="text-stone-500 mb-4">Generiere dein erstes Video im Content Generator</p>
          <NavLink to="/generator" className="btn-primary">
            Zum Generator
          </NavLink>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {media.map((item) => (
            <div 
              key={item.id} 
              className="bg-white rounded-xl border border-stone-200 overflow-hidden card-hover"
            >
              <div className="aspect-video bg-stone-100 flex items-center justify-center">
                {item.url ? (
                  <video src={item.url} className="w-full h-full object-cover" controls />
                ) : (
                  <div className="text-center p-4">
                    <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-stone-400" />
                    <p className="text-sm text-stone-500">{item.status}</p>
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="font-medium text-stone-900 truncate">{item.filename}</p>
                <p className="text-sm text-stone-500 mt-1 line-clamp-2">{item.prompt}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default MonthlyPlanPage;
export { ContentCalendar, Campaigns, MediaLibrary };
